'use client'
import {
  __privateAdd,
  __privateGet,
  __privateSet,
  theme
} from "./chunk-MQD3SWK3.mjs";

// src/bulk-actions/bulk-actions.tsx
import { runIfFn } from "@chakra-ui/utils";
import {
  Banner,
  BannerActions,
  BannerContent,
  BannerTitle
} from "@saas-ui/react";
import { jsx, jsxs } from "react/jsx-runtime";
var BulkActions = (props) => {
  const {
    selections = [],
    title = ":selections selected",
    children,
    ...rest
  } = props;
  const isOpen = !!selections?.length;
  return /* @__PURE__ */ jsxs(
    Banner,
    {
      initial: "exit",
      isOpen,
      colorScheme: "gray",
      variant: "floating",
      position: "absolute",
      minH: "14",
      zIndex: "banner",
      ...rest,
      children: [
        /* @__PURE__ */ jsx(BannerContent, { children: /* @__PURE__ */ jsx(BannerTitle, { children: title.replace(":selections", selections.length.toString()) }) }),
        /* @__PURE__ */ jsx(BannerActions, { flexShrink: 0, children: runIfFn(children, { selections }) })
      ]
    }
  );
};
BulkActions.displayName = "BulkActions";

// src/command/command.tsx
import { Kbd, chakra, useStyleConfig } from "@chakra-ui/react";
import { cx } from "@chakra-ui/utils";
import { Fragment, jsx as jsx2 } from "react/jsx-runtime";
var Key = ({ children }) => {
  if (typeof children !== "string") {
    return /* @__PURE__ */ jsx2(Fragment, { children });
  }
  if (["then", "or", "+"].includes(children)) {
    return /* @__PURE__ */ jsx2(chakra.span, { children });
  }
  return /* @__PURE__ */ jsx2(Kbd, { children });
};
var Command = (props) => {
  const { children, className, ...rest } = props;
  if (typeof children !== "string") {
    return /* @__PURE__ */ jsx2(Fragment, { children });
  }
  const keys = children.split(/\s|\+/);
  const styles = useStyleConfig("SuiCommand");
  const commandStyles = {
    "& > *:not(style) ~ *:not(style)": { marginEnd: 1 },
    color: "muted",
    ...styles
  };
  return /* @__PURE__ */ jsx2(
    chakra.span,
    {
      className: cx("sui-command", className),
      __css: commandStyles,
      ...rest,
      children: keys.map((key) => /* @__PURE__ */ jsx2(Key, { children: key }, key))
    }
  );
};

// src/data-grid/data-grid.tsx
import * as React4 from "react";
import {
  Table,
  chakra as chakra7,
  useCallbackRef,
  useMergeRefs,
  useMultiStyleConfig,
  useTheme
} from "@chakra-ui/react";
import { cx as cx2, runIfFn as runIfFn6 } from "@chakra-ui/utils";
import {
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from "@tanstack/react-table";

// src/data-grid/cell-selection/cell-selection-feature.ts
import { functionalUpdate, makeStateUpdater } from "@tanstack/react-table";

// src/data-grid/focus-model/utils.ts
var closest = (target, selector) => {
  const el = target;
  if (el.matches(selector)) {
    return el;
  }
  return el.closest(selector);
};

// src/data-grid/cell-selection/cell-selection-model.ts
var _startCell, _selectedCells, _isMouseDown;
var CellSelectionModel = class {
  constructor(rootEl, options) {
    this.rootEl = rootEl;
    this.options = options;
    __privateAdd(this, _startCell, null);
    __privateAdd(this, _selectedCells, []);
    __privateAdd(this, _isMouseDown, false);
    this.handleMouseDown = (e) => {
      const cell = this.getCellFromEvent(e);
      if (!cell) {
        return;
      }
      if (e.button === 2 && this.isInRange(cell)) {
        return;
      }
      if (!e.ctrlKey && !e.shiftKey) {
        __privateSet(this, _selectedCells, []);
        if (!__privateGet(this, _isMouseDown)) {
          __privateSet(this, _startCell, cell);
        }
      }
      if (e.ctrlKey) {
        __privateSet(this, _selectedCells, __privateGet(this, _selectedCells).filter(
          (c) => c[0] !== cell[0] && c[1] !== cell[1]
        ));
        if (!__privateGet(this, _isMouseDown)) {
          __privateSet(this, _startCell, cell);
        }
      }
      if (e.shiftKey && __privateGet(this, _startCell)) {
        __privateSet(this, _selectedCells, this.getCellsBetween(__privateGet(this, _startCell), cell));
      }
      this.updateSelection();
      __privateSet(this, _isMouseDown, true);
    };
    this.handleMouseOver = (e) => {
      if (e.buttons !== 1 || !__privateGet(this, _isMouseDown) || !__privateGet(this, _startCell)) {
        return;
      }
      const currentCell = this.getCellFromEvent(e);
      if (currentCell) {
        __privateSet(this, _selectedCells, this.getCellsBetween(__privateGet(this, _startCell), currentCell));
        this.updateSelection();
      }
    };
    this.handleMouseUp = () => {
      __privateSet(this, _isMouseDown, false);
    };
    this.getCellFromEvent = (e) => {
      if (!e.target) {
        return;
      }
      const td = closest(e.target, "td");
      if (!td) {
        return;
      }
      const tr = closest(td, "tr");
      if (!tr) {
        return;
      }
      const col = Number.parseInt(td.dataset.col);
      const row = Number.parseInt(tr.dataset.row);
      return [row, col];
    };
    this.getCellsBetween = (cell1, cell2) => {
      const [startRow, endRow] = [
        Math.min(cell1[0], cell2[0]),
        Math.max(cell1[0], cell2[0])
      ];
      const [startCol, endCol] = [
        Math.min(cell1[1], cell2[1]),
        Math.max(cell1[1], cell2[1])
      ];
      const cells = [];
      for (let row = startRow; row <= endRow; row++) {
        for (let col = startCol; col <= endCol; col++) {
          cells.push([row, col]);
        }
      }
      return cells;
    };
    this.isInRange = (cell) => {
      return __privateGet(this, _selectedCells).some((c) => c[0] === cell[0] && c[1] === cell[1]);
    };
    this.init();
  }
  init() {
    this.rootEl.addEventListener("mousedown", this.handleMouseDown);
    this.rootEl.addEventListener("mouseup", this.handleMouseUp);
    this.rootEl.addEventListener("mouseover", this.handleMouseOver);
  }
  updateSelection() {
    this.rootEl.querySelectorAll("[data-range-selected]").forEach((el) => {
      el.removeAttribute("data-range-selected");
    });
    __privateGet(this, _selectedCells).forEach(([row, col]) => {
      const cell = this.rootEl.querySelector(
        `tr[data-row="${row}"] td[data-col="${col}"]`
      );
      if (cell) {
        cell.setAttribute("data-range-selected", "");
      }
    });
    this.options.onSelectionChange?.(__privateGet(this, _selectedCells));
  }
  destroy() {
    this.rootEl.removeEventListener("mousedown", this.handleMouseDown);
    this.rootEl.removeEventListener("mouseup", this.handleMouseUp);
    this.rootEl.removeEventListener("mouseover", this.handleMouseOver);
  }
};
_startCell = new WeakMap();
_selectedCells = new WeakMap();
_isMouseDown = new WeakMap();

// src/data-grid/cell-selection/cell-selection-feature.ts
var CellSelectionFeature = {
  getInitialState: (state) => ({
    cellSelection: {
      selectedCells: []
    },
    ...state
  }),
  getDefaultOptions: (table) => ({
    experimental_enableCellSelection: false,
    onCellSelectionChange: makeStateUpdater("cellSelection", table)
  }),
  createTable: (table) => {
    let selectionModel = null;
    table.setRootNode = (node) => {
      if (!selectionModel && table.options.experimental_enableCellSelection) {
        selectionModel = new CellSelectionModel(node, {
          onSelectionChange: (selectedCells) => {
            table.setSelectedCells(() => ({ selectedCells }));
          }
        });
      }
    };
    table.getCellSelectionModel = () => {
      return selectionModel;
    };
    table.setSelectedCells = (updater) => {
      return table.options.onCellSelectionChange?.((old) => {
        return functionalUpdate(updater, old);
      });
    };
    table.onUnmount = () => {
      selectionModel?.destroy();
      selectionModel = null;
    };
  },
  createCell: (cell, column, row, table) => {
    cell.getIsInSelectionRange = () => {
      return table.getCellSelectionModel()?.isInRange([row.index, column.getIndex()]) ?? false;
    };
  }
};

// src/data-grid/data-grid-body.tsx
import { memo as memo3 } from "react";
import { Tbody } from "@chakra-ui/react";

// src/data-grid/data-grid-row.tsx
import { memo as memo2, useCallback, useMemo as useMemo2 } from "react";
import { Tr } from "@chakra-ui/react";
import { callAllHandlers, dataAttr as dataAttr2, runIfFn as runIfFn3 } from "@chakra-ui/utils";

// src/data-grid/data-grid-cell.tsx
import { memo, useMemo } from "react";
import { Td } from "@chakra-ui/react";
import { dataAttr, runIfFn as runIfFn2 } from "@chakra-ui/utils";
import { flexRender } from "@tanstack/react-table";

// src/data-grid/data-grid.utils.ts
var escapeId = (id) => id.replaceAll(/[\s.]/g, "-");
var getResult = (fn, params) => {
  if (typeof fn === "function") {
    return fn(params);
  }
  return fn;
};

// src/data-grid/utils.ts
var isGroupColumn = (column) => "columnDefType" in column.columnDef && column.columnDef.columnDefType === "group";
var getPinnedStyles = (column) => {
  const isColumnPinned = !isGroupColumn(column) && column.getIsPinned();
  if (!isColumnPinned) {
    return {};
  }
  return isColumnPinned === "left" ? {
    "--pinned-left": column.getStart("left") + "px"
  } : {
    "--pinned-right": column.getAfter("right") + "px"
  };
};

// src/data-grid/data-grid-cell.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
function DataGridCell(props) {
  const { instance, cell, index, slotProps } = props;
  const column = cell.column;
  const meta = column.columnDef.meta ?? {};
  const colId = escapeId(column.id);
  const cellProps = useMemo(
    () => runIfFn2(slotProps?.cell, {
      cell,
      table: instance
    }),
    [slotProps, cell, instance]
  );
  const isColumnPinned = !isGroupColumn(column) && column.getIsPinned();
  const pinnedStyles = useMemo(() => getPinnedStyles(column), [column]);
  const isLast = column.getIsLastColumn(isColumnPinned);
  return /* @__PURE__ */ jsx3(
    Td,
    {
      isNumeric: meta.isNumeric,
      "data-col": index,
      "data-pinned": isColumnPinned ? isColumnPinned : void 0,
      "data-range-selected": cell.getIsInSelectionRange() ? "" : void 0,
      "data-last": dataAttr(isLast),
      userSelect: instance.options.experimental_enableCellSelection ? "none" : void 0,
      flexBasis: `calc(var(--col-${colId}-size) * 1px)`,
      flexShrink: 0,
      flexGrow: "var(--column-grow, 1)",
      width: `calc(var(--col-${colId}-size) * 1px)`,
      minWidth: `max(var(--col-${colId}-size) * 1px, 40px)`,
      ...meta.cellProps,
      ...cellProps,
      style: pinnedStyles,
      children: flexRender(cell.column.columnDef.cell, cell.getContext())
    }
  );
}
var MemoizedDataGridCell = memo(DataGridCell, (prev, next) => {
  return prev.cell === next.cell;
});

// src/data-grid/data-grid-row.tsx
import { jsx as jsx4, jsxs as jsxs2 } from "react/jsx-runtime";
var DataGridRow = function DataGridRow2(props) {
  const {
    instance,
    slotProps,
    row,
    rowIndex,
    virtualRow,
    virtualizer,
    isSelectable,
    isExpandable,
    isHoverable,
    onClick,
    focusModel
  } = props;
  const visibleCells = row.getVisibleCells();
  const virtualColumns = virtualizer.column?.getVirtualItems();
  const columns = virtualColumns ?? visibleCells;
  const ariaProps = {};
  if (isExpandable) {
    ariaProps["aria-expanded"] = row.getIsExpanded();
  }
  if (isSelectable) {
    ariaProps["aria-selected"] = row.getIsSelected();
  }
  const rowProps = useMemo2(
    () => runIfFn3(slotProps?.row, {
      row,
      table: instance
    }),
    [slotProps, row, instance]
  );
  const { virtualPaddingLeft, virtualPaddingRight } = virtualizer.column ?? {};
  const memoizedStyle = useMemo2(
    () => ({
      "--row-depth": String(row.depth),
      position: virtualRow ? "absolute" : void 0,
      top: virtualRow ? "0" : void 0,
      transform: virtualRow ? `translateY(${virtualRow?.start}px)` : void 0
    }),
    [row.depth, virtualRow]
  );
  const memoizedOnClick = useCallback(
    (e) => callAllHandlers(onClick, props?.onClick)(e),
    [onClick, props?.onClick]
  );
  return /* @__PURE__ */ jsxs2(
    Tr,
    {
      ...rowProps,
      ref: (el) => {
        virtualizer?.row?.measureElement(el);
      },
      onClick: memoizedOnClick,
      "data-row": rowIndex,
      "data-selected": dataAttr2(row.getIsSelected()),
      "data-interactive": dataAttr2(isHoverable),
      ...ariaProps,
      ...focusModel?.getRowProps(row),
      style: memoizedStyle,
      children: [
        virtualPaddingLeft ? /* @__PURE__ */ jsx4("td", { style: { display: "flex", width: virtualPaddingLeft } }) : null,
        columns.map((cellOrVirtual, index) => {
          let cell = cellOrVirtual;
          if (virtualizer.column && "index" in cellOrVirtual) {
            cell = visibleCells[cellOrVirtual.index];
            index = cellOrVirtual.index;
          }
          const key = `${cell.id}`;
          return /* @__PURE__ */ jsx4(
            DataGridCell,
            {
              slotProps,
              instance,
              cell,
              index
            },
            key
          );
        }),
        virtualPaddingRight ? /* @__PURE__ */ jsx4("td", { style: { display: "flex", width: virtualPaddingRight } }) : null
      ]
    }
  );
};
DataGridRow.displayName = "DataGridRow";
var MemoizedDataGridRow = memo2(DataGridRow, (prev, next) => {
  return prev.row === next.row && prev.rowIndex === next.rowIndex;
});

// src/data-grid/data-grid-virtualizer.tsx
import { useCallback as useCallback2, useMemo as useMemo3 } from "react";
import {
  defaultRangeExtractor,
  useVirtualizer
} from "@tanstack/react-virtual";
function useColumnVirtualizer(instance, options) {
  if (options.enabled === false) return null;
  const { getState, getIsSomeColumnsPinned } = instance;
  const visibleColumns = instance.getVisibleLeafColumns();
  const { columnPinning, columnVisibility } = getState();
  const enableColumnPinning = getIsSomeColumnsPinned();
  const [leftPinnedIndexes, rightPinnedIndexes] = useMemo3(
    () => enableColumnPinning ? [
      instance.getLeftVisibleLeafColumns().map((c) => c.getPinnedIndex()),
      instance.getRightVisibleLeafColumns().map(
        (column) => visibleColumns.length - column.getPinnedIndex() - 1
      ).sort((a, b) => a - b)
    ] : [[], []],
    [columnPinning, columnVisibility, enableColumnPinning]
  );
  const numPinnedLeft = leftPinnedIndexes.length;
  const numPinnedRight = rightPinnedIndexes.length;
  const columnVirtualizer = useVirtualizer({
    count: visibleColumns.length,
    estimateSize: (index) => visibleColumns[index].getSize(),
    horizontal: true,
    overscan: 3,
    indexAttribute: "data-col",
    rangeExtractor: useCallback2(
      (range) => {
        const indexes = extraIndexRangeExtractor(range);
        if (!numPinnedLeft && !numPinnedRight) {
          return indexes;
        }
        return [
          .../* @__PURE__ */ new Set([...leftPinnedIndexes, ...indexes, ...rightPinnedIndexes])
        ];
      },
      [numPinnedLeft, numPinnedRight]
    ),
    ...options
  });
  const virtualColumns = columnVirtualizer?.getVirtualItems();
  const numColumns = virtualColumns.length;
  let virtualPaddingLeft;
  let virtualPaddingRight;
  if (numColumns > 0) {
    const totalSize = columnVirtualizer.getTotalSize();
    const leftNonPinnedStart = virtualColumns[numPinnedLeft]?.start || 0;
    const leftNonPinnedEnd = virtualColumns[leftPinnedIndexes.length - 1]?.end || 0;
    const rightNonPinnedStart = virtualColumns[numColumns - numPinnedRight]?.start || 0;
    const rightNonPinnedEnd = virtualColumns[numColumns - numPinnedRight - 1]?.end || 0;
    virtualPaddingLeft = leftNonPinnedStart - leftNonPinnedEnd;
    virtualPaddingRight = totalSize - rightNonPinnedEnd - (numPinnedRight ? totalSize - rightNonPinnedStart : 0);
  }
  return {
    ...columnVirtualizer,
    virtualPaddingLeft,
    virtualPaddingRight
  };
}
function useRowVirtualizer(rows, options) {
  if (options.enabled === false) return null;
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    indexAttribute: "data-row",
    overscan: 5,
    ...options
  });
  return rowVirtualizer;
}
var extraIndexRangeExtractor = (range, draggingIndex) => {
  const newIndexes = defaultRangeExtractor(range);
  if (draggingIndex === void 0) return newIndexes;
  if (draggingIndex >= 0 && draggingIndex < Math.max(range.startIndex - range.overscan, 0)) {
    newIndexes.unshift(draggingIndex);
  }
  if (draggingIndex >= 0 && draggingIndex > range.endIndex + range.overscan) {
    newIndexes.push(draggingIndex);
  }
  return newIndexes;
};

// src/data-grid/data-grid-body.tsx
import { jsx as jsx5 } from "react/jsx-runtime";
function DataGridBody(props) {
  const {
    instance,
    scrollRef,
    size,
    columnVirtualizer,
    rowVirtualizerOptions,
    slotProps,
    isSelectable,
    isExpandable,
    isHoverable,
    onRowClick,
    focusModel
  } = props;
  const rows = instance.getRowModel().rows;
  const rowVirtualizer = useRowVirtualizer(rows, {
    estimateSize: () => {
      switch (size) {
        case "xl":
          return 65;
        case "lg":
          return 53;
        case "sm":
          return 33;
        case "md":
        default:
          return 41;
      }
    },
    getScrollElement: () => scrollRef.current,
    ...rowVirtualizerOptions
  });
  const virtualRows = rowVirtualizer?.getVirtualItems();
  const bodyHeight = rowVirtualizer?.getTotalSize();
  return /* @__PURE__ */ jsx5(
    Tbody,
    {
      position: "relative",
      style: {
        height: bodyHeight ? bodyHeight + "px" : void 0
      },
      children: (virtualRows ?? rows).map((rowOrVirtualRow, index) => {
        let row = rowOrVirtualRow;
        if (rowVirtualizer) {
          row = rows[rowOrVirtualRow.index];
          index = rowOrVirtualRow.index;
        }
        if (!row) {
          return null;
        }
        const props2 = {
          instance,
          slotProps,
          row,
          rowIndex: index,
          virtualRow: rowVirtualizer ? rowOrVirtualRow : void 0,
          virtualizer: {
            row: rowVirtualizer,
            column: columnVirtualizer
          },
          isSelectable,
          isExpandable,
          isHoverable,
          ...focusModel?.getRowProps(row),
          onClick: (e) => onRowClick?.(row, e)
        };
        const key = `${row.id}`;
        return /* @__PURE__ */ jsx5(DataGridRow, { ...props2 }, key);
      })
    }
  );
}
var MemoizedDataGridBody = memo3(DataGridBody);

// src/data-grid/data-grid-cell-value.tsx
import { chakra as chakra2 } from "@chakra-ui/react";
import { Link } from "@saas-ui/react";
import { jsx as jsx6 } from "react/jsx-runtime";
var DataGridCellValue = (props) => {
  const { column, row, getValue } = props;
  const meta = column.columnDef.meta || {};
  let content = getValue();
  if (meta.href) {
    const href = getResult(meta.href, row.original);
    content = /* @__PURE__ */ jsx6(Link, { href, children: content });
  }
  if (typeof content === "string") {
    content = /* @__PURE__ */ jsx6(
      chakra2.span,
      {
        sx: meta.isTruncated !== false ? {
          textOverflow: "ellipsis",
          overflow: "hidden",
          whiteSpace: "nowrap"
        } : {},
        children: content
      }
    );
  }
  return content;
};
DataGridCellValue.displayName = "DataGridCellValue";
var DefaultDataGridCell = DataGridCellValue;

// src/data-grid/data-grid-checkbox.tsx
import React2 from "react";
import {
  Checkbox,
  VisuallyHidden,
  chakra as chakra3,
  forwardRef
} from "@chakra-ui/react";

// src/data-grid/data-grid-context.tsx
import React from "react";

// src/data-grid/data-grid-translations.tsx
var defaultTranslations = {
  page: "Page",
  of: "of {pageCount}",
  nextPage: "Next page",
  previousPage: "Previous page",
  expandRows: "Expand all rows",
  collapseRows: "Collapse all rows",
  deselectAllRows: "Deselect all rows",
  selectAllRows: "Select all rows",
  selectRow: "Select row",
  deselectRow: "Deselect row",
  sortAscending: "Sort ascending",
  sortDescending: "Sort descending"
};

// src/data-grid/data-grid-context.tsx
import { jsx as jsx7 } from "react/jsx-runtime";
var DataGridContext = React.createContext(null);
var DataGridProvider = (props) => {
  const {
    instance,
    children,
    colorScheme,
    variant,
    size,
    icons,
    translations
  } = props;
  const state = instance.getState();
  const context = React.useMemo(
    () => ({
      state,
      instance,
      colorScheme,
      variant,
      size,
      icons,
      translations: {
        ...defaultTranslations,
        ...translations
      }
    }),
    [instance, state, colorScheme, variant, size, icons, translations]
  );
  return /* @__PURE__ */ jsx7(DataGridContext.Provider, { value: context, children });
};
var useDataGridContext = () => {
  return React.useContext(DataGridContext);
};
var useDataGridIcons = () => {
  const { icons } = useDataGridContext();
  return icons;
};

// src/data-grid/data-grid-checkbox.tsx
import { jsx as jsx8 } from "react/jsx-runtime";
var getSelectionColumn = (enabled, columnDef) => {
  return enabled ? [
    {
      id: "selection",
      size: 48,
      maxSize: 48,
      minSize: 48,
      enableHiding: false,
      enableSorting: false,
      enableColumnFilter: false,
      enableGlobalFilter: false,
      enableGrouping: false,
      enableMultiSort: false,
      enableResizing: false,
      header: ({ table }) => /* @__PURE__ */ jsx8(
        DataGridCheckbox,
        {
          isChecked: table.getIsAllRowsSelected(),
          isIndeterminate: table.getIsSomeRowsSelected(),
          onChange: table.getToggleAllRowsSelectedHandler()
        }
      ),
      cell: ({ row }) => /* @__PURE__ */ jsx8(
        DataGridCheckbox,
        {
          isChecked: row.getIsSelected(),
          isIndeterminate: row.getIsSomeSelected(),
          isDisabled: !row.getCanSelect(),
          onChange: row.getToggleSelectedHandler(),
          isRow: true
        }
      ),
      meta: {
        headerProps: {
          flex: 0
        },
        titleProps: {
          py: 0
        },
        cellProps: {
          flex: 0
        }
      },
      ...columnDef
    }
  ] : [];
};
var DataGridCheckbox = forwardRef((props, ref) => {
  const { isRow, ...rest } = props;
  const onClick = React2.useCallback(
    (e) => e.stopPropagation(),
    []
  );
  const { colorScheme, translations } = useDataGridContext();
  let label = props.isChecked ? translations.deselectRow : translations.selectRow;
  if (!isRow) {
    label = props.isChecked ? translations.deselectAllRows : translations.selectAllRows;
  }
  return /* @__PURE__ */ jsx8(
    chakra3.div,
    {
      onClick,
      sx: {
        display: "inline-flex",
        "& .chakra-checkbox__label": {
          ms: 0
        }
      },
      children: /* @__PURE__ */ jsx8(
        Checkbox,
        {
          ref,
          display: "inline-flex",
          verticalAlign: "middle",
          colorScheme,
          ...rest,
          children: /* @__PURE__ */ jsx8(VisuallyHidden, { children: label })
        }
      )
    }
  );
});

// src/data-grid/data-grid-expander.tsx
import { IconButton, forwardRef as forwardRef2 } from "@chakra-ui/react";

// src/utils/create-icon.tsx
import { createIcon as _createIcon } from "@chakra-ui/react";
var createIcon = (props) => {
  return _createIcon({
    viewBox: "0 0 24 24",
    defaultProps: {
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    },
    ...props
  });
};

// src/icons/icons.tsx
import { Fragment as Fragment2, jsx as jsx9, jsxs as jsxs3 } from "react/jsx-runtime";
var ChevronUpIcon = createIcon({
  displayName: "ChevronUpIcon",
  path: /* @__PURE__ */ jsx9("polyline", { points: "18 15 12 9 6 15" })
});
var ChevronDownIcon = createIcon({
  displayName: "ChevronDownIcon",
  path: /* @__PURE__ */ jsx9("polyline", { points: "6 9 12 15 18 9" })
});
var ChevronUpDownIcon = createIcon({
  displayName: "ChevronUpDownIcon",
  path: /* @__PURE__ */ jsxs3(Fragment2, { children: [
    /* @__PURE__ */ jsx9("path", { d: "m7 15 5 5 5-5" }),
    /* @__PURE__ */ jsx9("path", { d: "m7 9 5-5 5 5" })
  ] })
});
var ChevronLeftIcon = createIcon({
  displayName: "ChevronLeftIcon",
  path: /* @__PURE__ */ jsx9("polyline", { points: "15 18 9 12 15 6" })
});
var ChevronRightIcon = createIcon({
  displayName: "ChevronRightIcon",
  path: /* @__PURE__ */ jsx9("polyline", { points: "9 18 15 12 9 6" })
});
var HamburgerIcon = createIcon({
  displayName: "ChevronDownIcon",
  path: /* @__PURE__ */ jsxs3("g", { fill: "none", children: [
    /* @__PURE__ */ jsx9("line", { x1: "3", y1: "12", x2: "21", y2: "12" }),
    /* @__PURE__ */ jsx9("line", { x1: "3", y1: "6", x2: "21", y2: "6" }),
    /* @__PURE__ */ jsx9("line", { x1: "3", y1: "18", x2: "21", y2: "18" })
  ] })
});
var XIcon = createIcon({
  displayName: "XIcon",
  path: /* @__PURE__ */ jsxs3("g", { children: [
    /* @__PURE__ */ jsx9("line", { x1: "18", y1: "6", x2: "6", y2: "18" }),
    /* @__PURE__ */ jsx9("line", { x1: "6", y1: "6", x2: "18", y2: "18" })
  ] })
});
var FilterIcon = createIcon({
  displayName: "FilterIcon",
  path: /* @__PURE__ */ jsx9("polygon", { points: "22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" })
});

// src/data-grid/data-grid-expander.tsx
import { jsx as jsx10 } from "react/jsx-runtime";
var DataGridExpander = forwardRef2(
  (props, ref) => {
    const { isExpanded, onToggle, ...rest } = props;
    const { instance, icons, translations } = useDataGridContext();
    if (!instance.getCanSomeRowsExpand()) {
      return null;
    }
    const expandedIcon = icons?.rowExpanded ?? /* @__PURE__ */ jsx10(ChevronDownIcon, {});
    const collapsedIcon = icons?.rowCollapsed ?? /* @__PURE__ */ jsx10(ChevronRightIcon, {});
    return /* @__PURE__ */ jsx10(
      IconButton,
      {
        ref,
        size: "xs",
        variant: "ghost",
        fontSize: "1.2em",
        ...rest,
        "aria-label": isExpanded ? translations.collapseRows : translations.expandRows,
        icon: isExpanded ? expandedIcon : collapsedIcon,
        onClick: onToggle
      }
    );
  }
);
var getExpanderColumn = (enabled, columnDef) => {
  return enabled ? [
    {
      id: "expand",
      header: ({ table, column }) => {
        const meta = column.columnDef.meta || {};
        return /* @__PURE__ */ jsx10(
          DataGridExpander,
          {
            ...meta.expanderProps,
            isExpanded: table.getIsAllRowsExpanded(),
            onToggle: table.getToggleAllRowsExpandedHandler()
          }
        );
      },
      size: 40,
      minSize: 40,
      enableSorting: false,
      enableColumnFilter: false,
      enableGlobalFilter: false,
      enableGrouping: false,
      enableMultiSort: false,
      enableResizing: false,
      meta: {
        headerProps: {
          flex: "0 0 calc(40px + var(--expanded-depth) * 4px)"
        },
        titleProps: {
          p: 2
        },
        cellProps: {
          py: 2,
          flex: "0 0 calc(40px + var(--expanded-depth) * 4px)",
          textOverflow: "initial",
          ps: "calc(calc(var(--row-depth) + 1) * 0.5rem)"
        }
      },
      cell: ({ row, column }) => {
        const meta = column.columnDef.meta || {};
        return row.getCanExpand() ? /* @__PURE__ */ jsx10(
          DataGridExpander,
          {
            ...meta.expanderProps,
            isExpanded: row.getIsExpanded(),
            onToggle: row.getToggleExpandedHandler()
          }
        ) : null;
      },
      ...columnDef
    }
  ] : [];
};

// src/data-grid/data-grid-footer.tsx
import { useMemo as useMemo4 } from "react";
import { Td as Td2, Tfoot, Tr as Tr2 } from "@chakra-ui/react";
import { runIfFn as runIfFn4 } from "@chakra-ui/utils";
import { flexRender as flexRender2 } from "@tanstack/react-table";
import { jsx as jsx11, jsxs as jsxs4 } from "react/jsx-runtime";
function DataGridFooter(props) {
  const { instance, slotProps, columnVirtualizer } = props;
  const footerGroups = instance.getFooterGroups();
  const hasFooter = useMemo4(
    () => footerGroups.some(
      (footerGroup) => footerGroup.headers.some(
        (header) => !header.isPlaceholder && header.column.columnDef.footer
      )
    ),
    [footerGroups]
  );
  if (!hasFooter) return null;
  const virtualColumns = columnVirtualizer?.getVirtualItems();
  return /* @__PURE__ */ jsx11(Tfoot, { children: footerGroups.map((footerGroup) => {
    const headers = virtualColumns ?? footerGroup.headers;
    return /* @__PURE__ */ jsxs4(Tr2, { children: [
      columnVirtualizer?.virtualPaddingLeft ? /* @__PURE__ */ jsx11(
        "th",
        {
          style: {
            display: "flex",
            width: columnVirtualizer.virtualPaddingLeft
          }
        }
      ) : null,
      headers.map((vc) => {
        const header = "column" in vc ? vc : footerGroup.headers[vc.index];
        const colId = escapeId(header.id);
        const footerProps = runIfFn4(slotProps?.footer, {
          header,
          table: instance
        });
        return /* @__PURE__ */ jsx11(
          Td2,
          {
            flex: `1 0 calc(var(--col-${colId}-size) * 1px)`,
            width: `calc(var(--col-${colId}-size) * 1px)`,
            minWidth: `max(var(--col-${colId}-size) * 1px, 40px)`,
            ...footerProps,
            children: header.isPlaceholder ? null : flexRender2(
              header.column.columnDef.footer,
              header.getContext()
            )
          },
          header.id
        );
      }),
      columnVirtualizer?.virtualPaddingRight ? /* @__PURE__ */ jsx11(
        "th",
        {
          style: {
            display: "flex",
            width: columnVirtualizer.virtualPaddingRight
          }
        }
      ) : null
    ] }, footerGroup.id);
  }) });
}

// src/data-grid/data-grid-header.tsx
import { Thead, Tr as Tr3 } from "@chakra-ui/react";
import { dataAttr as dataAttr4, runIfFn as runIfFn5 } from "@chakra-ui/utils";

// src/data-grid/data-grid-header-cell.tsx
import { Th, chakra as chakra6, useTableStyles as useTableStyles2 } from "@chakra-ui/react";
import { dataAttr as dataAttr3 } from "@chakra-ui/utils";
import { flexRender as flexRender3 } from "@tanstack/react-table";

// src/data-grid/data-grid-column-resizer.tsx
import { chakra as chakra4, useEnvironment, useTableStyles } from "@chakra-ui/react";
import { jsx as jsx12 } from "react/jsx-runtime";
var DataGridColumnResizer = (props) => {
  const { header, ...rest } = props;
  const styles = useTableStyles();
  if (!header.column.getCanResize()) {
    return null;
  }
  const env = useEnvironment();
  const document2 = env.getDocument();
  return /* @__PURE__ */ jsx12(
    chakra4.div,
    {
      __css: styles.resizer,
      className: "sui-data-grid__resizer",
      ...rest,
      onDoubleClick: () => header.column.resetSize(),
      onMouseDown: header.getResizeHandler(document2),
      onTouchStart: header.getResizeHandler(document2)
    }
  );
};
DataGridColumnResizer.displayName = "DataGridColumnResizer";

// src/data-grid/data-grid-sort.tsx
import { chakra as chakra5 } from "@chakra-ui/react";
import { jsx as jsx13 } from "react/jsx-runtime";
var DataGridSort = (props) => {
  const { header, ...rest } = props;
  const sorterStyles = {
    _focusVisible: {
      outline: "none",
      boxShadow: "outline"
    },
    ms: 2
  };
  const { icons, translations } = useDataGridContext();
  const sortDescendingIcon = icons?.sortDescending ?? /* @__PURE__ */ jsx13(ChevronDownIcon, {});
  const sortAscendingIcon = icons?.sortAscending ?? /* @__PURE__ */ jsx13(ChevronUpIcon, {});
  const sortIcon = icons?.sort ?? /* @__PURE__ */ jsx13(ChevronUpDownIcon, {});
  if (header.id === "selection" || !header.column.getCanSort()) {
    return null;
  }
  const sorted = header.column.getIsSorted();
  const isDesc = sorted === "desc";
  return /* @__PURE__ */ jsx13(
    chakra5.span,
    {
      fontSize: "1.2em",
      display: "inline-flex",
      alignItems: "center",
      role: "presentation",
      "aria-label": isDesc ? translations.sortAscending : translations.sortDescending,
      __css: sorterStyles,
      ...rest,
      children: sorted ? isDesc ? sortDescendingIcon : sortAscendingIcon : sortIcon
    }
  );
};
DataGridSort.displayName = "DataGridSort";

// src/data-grid/data-grid-header-cell.tsx
import { jsx as jsx14, jsxs as jsxs5 } from "react/jsx-runtime";
var DataGridHeaderCell = (props) => {
  const { header, isSortable, ...rest } = props;
  const styles = useTableStyles2();
  let titleProps = {};
  const column = header.column;
  if (isSortable && column.getCanSort()) {
    const sorted = column.getIsSorted();
    titleProps = {
      userSelect: "none",
      cursor: "pointer",
      tabIndex: 0,
      "aria-sort": sorted ? sorted === "desc" ? "descending" : "ascending" : "none",
      onClick: column.getToggleSortingHandler(),
      onKeyDown: (event) => {
        if (event.key === "Enter") {
          column.toggleSorting();
        }
      }
    };
  }
  const isColumnPinned = !isGroupColumn(column) && column.getIsPinned();
  const isLast = column.getIsLastColumn(isColumnPinned);
  const headerStyle = getPinnedStyles(column);
  const meta = header.column.columnDef.meta || {};
  const colId = escapeId(header.id);
  return /* @__PURE__ */ jsxs5(
    Th,
    {
      scope: "col",
      colSpan: header.colSpan,
      isNumeric: meta.isNumeric,
      "data-pinned": isColumnPinned ? isColumnPinned : void 0,
      "data-last": dataAttr3(isLast),
      flexBasis: `calc(var(--header-${colId}-size) * 1px)`,
      flexShrink: 0,
      flexGrow: "var(--column-grow, 1)",
      width: `calc(var(--header-${colId}-size) * 1px)`,
      minWidth: `max(var(--col-${colId}-size) * 1px, 40px)`,
      ...meta.headerProps,
      ...rest,
      style: {
        ...headerStyle,
        ...meta.headerProps?.style
      },
      children: [
        /* @__PURE__ */ jsxs5(
          chakra6.div,
          {
            __css: styles.title,
            className: "sui-data-grid__title",
            ...meta.titleProps,
            ...titleProps,
            children: [
              flexRender3(header.column.columnDef.header, header.getContext()),
              isSortable && /* @__PURE__ */ jsx14(DataGridSort, { header })
            ]
          }
        ),
        /* @__PURE__ */ jsx14(DataGridColumnResizer, { header })
      ]
    }
  );
};
DataGridHeaderCell.displayName = "DataGridHeaderCell";

// src/data-grid/data-grid-header.tsx
import { jsx as jsx15, jsxs as jsxs6 } from "react/jsx-runtime";
function DataGridHeader(props) {
  const { instance, columnVirtualizer, stickyHeader, slotProps, isSortable } = props;
  const virtualColumns = columnVirtualizer?.getVirtualItems();
  return /* @__PURE__ */ jsx15(Thead, { "data-sticky": dataAttr4(stickyHeader), children: instance.getHeaderGroups().map((headerGroup) => /* @__PURE__ */ jsxs6(Tr3, { children: [
    columnVirtualizer?.virtualPaddingLeft ? /* @__PURE__ */ jsx15(
      "th",
      {
        style: {
          display: "flex",
          width: columnVirtualizer.virtualPaddingLeft
        }
      }
    ) : null,
    (virtualColumns ?? headerGroup.headers).map(
      (headerOrVirtualColumn, index) => {
        let header = headerOrVirtualColumn;
        if (columnVirtualizer) {
          header = headerGroup.headers[headerOrVirtualColumn.index];
          index = headerOrVirtualColumn.index;
        }
        const headerProps = runIfFn5(slotProps?.header, {
          header,
          table: instance
        });
        const key = `${header.id}-${index}`;
        return /* @__PURE__ */ jsx15(
          DataGridHeaderCell,
          {
            header,
            isSortable,
            ...headerProps
          },
          key
        );
      }
    ),
    columnVirtualizer?.virtualPaddingRight ? /* @__PURE__ */ jsx15(
      "th",
      {
        style: {
          display: "flex",
          width: columnVirtualizer.virtualPaddingRight
        }
      }
    ) : null
  ] }, headerGroup.id)) });
}

// src/data-grid/focus-model/focus-model.ts
var ROW_SELECTORS = 'tr, [role="row"]';
var BODY_ROW_SELECTORS = 'tbody tr, tbody [role="row"]';
var SELECTED_ROW_SELECTORS = 'tr[aria-selected="true"], [role="row"][aria-selected="true"]';
var CELL_SELECTORS = 'td, [role="gridcell"]';
var FOCUSABLE_SELECTORS = "a, button, input, textarea, select, [tabindex]";
var SPACEBAR = " ";
var closest2 = (target, selector) => {
  const el = target;
  if (el.matches(selector)) {
    return el;
  }
  return el.closest(selector);
};
var matches = (target, selector) => {
  return target.matches(selector);
};
var _focusedRow, _focusedCol, _focusedElement, _highlightedRow, _highlightedCol, _initialSelectedRow, _debug;
var FocusModel = class {
  constructor(gridEl, options) {
    this.gridEl = gridEl;
    this.options = options;
    __privateAdd(this, _focusedRow, 0);
    __privateAdd(this, _focusedCol, 0);
    __privateAdd(this, _focusedElement, null);
    __privateAdd(this, _highlightedRow, null);
    __privateAdd(this, _highlightedCol, null);
    __privateAdd(this, _initialSelectedRow, null);
    this.enabled = true;
    __privateAdd(this, _debug, (...args) => {
      if (this.options.debug === true) {
        console.debug("[FocusModel]", ...args);
      }
    });
    this.handleMouseDown = (e) => {
      const target = e.target;
      const mode = this.options.mode;
      const row = closest2(target, ROW_SELECTORS);
      if (!row || row.dataset.row === void 0) {
        return;
      }
      const rowIndex = Number.parseInt(row.dataset.row);
      if (mode === "list") {
        if (!matches(target, FOCUSABLE_SELECTORS)) {
          row.focus();
        }
        this.setFocusedRow(rowIndex);
      } else if (mode === "grid") {
        const cell = closest2(target, CELL_SELECTORS);
        if (!cell || cell.dataset.col === void 0) {
          return;
        }
        const colIndex = Number.parseInt(cell.dataset.col);
        this.setFocusedCol(rowIndex, colIndex);
      }
    };
    this.handleKeyDown = (e) => {
      const target = e.target;
      let focusedRow = __privateGet(this, _focusedRow);
      let focusedCol = __privateGet(this, _focusedCol);
      const mode = this.options.mode;
      const row = closest2(target, ROW_SELECTORS);
      const currentRowFocused = row && row.dataset.row === focusedRow.toString();
      const keyMap = {
        ArrowDown: () => {
          if (!currentRowFocused) {
            return;
          }
          const index = focusedRow + 1;
          if (!this.isValidRow(index)) {
            return;
          }
          e.preventDefault();
          if (e.shiftKey) {
            if (!__privateGet(this, _initialSelectedRow)) {
              __privateSet(this, _initialSelectedRow, focusedRow);
            }
            if (!this.hasSelectedRows()) {
              this.selectRows(focusedRow);
              return;
            }
            const isBefore2 = index < __privateGet(this, _initialSelectedRow);
            isBefore2 ? this.selectRows(index, __privateGet(this, _initialSelectedRow)) : this.selectRows(__privateGet(this, _initialSelectedRow), index);
          } else {
            __privateSet(this, _initialSelectedRow, null);
          }
          focusedRow = index;
        },
        ArrowUp: () => {
          if (!currentRowFocused) {
            return;
          }
          const index = focusedRow - 1;
          if (!this.isValidRow(index)) {
            return;
          }
          e.preventDefault();
          if (e.shiftKey) {
            if (!__privateGet(this, _initialSelectedRow)) {
              __privateSet(this, _initialSelectedRow, focusedRow);
            }
            if (!this.hasSelectedRows()) {
              this.selectRows(focusedRow);
              return;
            }
            const isAfter3 = index > __privateGet(this, _initialSelectedRow);
            isAfter3 ? this.selectRows(__privateGet(this, _initialSelectedRow), index) : this.selectRows(index, __privateGet(this, _initialSelectedRow));
          } else {
            __privateSet(this, _initialSelectedRow, null);
          }
          focusedRow = index;
        },
        Tab: () => {
          if (!currentRowFocused) {
            return;
          }
          if (e.shiftKey) {
            if (mode === "grid" && this.isValidCell(focusedRow, focusedCol - 1)) {
              focusedCol -= 1;
            } else if (this.isValidRow(focusedRow - 1)) {
              focusedRow -= 1;
            }
          } else {
            if (mode === "grid" && this.isValidCell(focusedRow, focusedCol + 1)) {
              focusedCol += 1;
            } else if (this.isValidRow(focusedRow + 1)) {
              focusedRow += 1;
            }
          }
        },
        ArrowRight: () => {
          if (mode === "grid" && this.isValidCell(focusedRow, focusedCol + 1)) {
            e.preventDefault();
            focusedCol += 1;
          } else if (mode === "list") {
            this.options.onExpandRow?.(focusedRow);
          }
        },
        ArrowLeft: () => {
          if (mode === "grid" && this.isValidCell(focusedRow, focusedCol - 1)) {
            e.preventDefault();
            focusedCol -= 1;
          } else if (mode === "list") {
            this.options.onCollapseRow?.(focusedRow);
          }
        },
        Home: () => {
          if (mode === "grid") {
            focusedCol = 0;
          }
          if (e.ctrlKey || mode === "list") {
            focusedRow = 0;
          }
        },
        End: () => {
          const rows = this.gridEl?.querySelectorAll(
            BODY_ROW_SELECTORS
          ) ?? [];
          const lastRowIndex = rows.length - 1;
          if (mode === "grid") {
            focusedCol = rows[lastRowIndex].querySelectorAll(
              CELL_SELECTORS
            )?.length - 1 || 0;
          }
          if (e.ctrlKey || mode === "list") {
            focusedRow = lastRowIndex;
          }
        },
        // space
        [SPACEBAR]: () => {
          const row2 = e.target && closest2(e.target, ROW_SELECTORS);
          const isFocusedRow = row2 && row2.dataset.row === focusedRow.toString();
          if (isFocusedRow && this.options.onToggleRowSelected) {
            this.options.onToggleRowSelected?.(focusedRow);
            e.preventDefault();
          }
        },
        Enter: () => {
          if (!currentRowFocused) {
            return;
          }
          const el = target.querySelector(FOCUSABLE_SELECTORS);
          if (el) {
            this.enabled = false;
            el.focus();
          }
        }
      };
      if (this.enabled) {
        keyMap[e.key]?.();
      } else if (e.key === "Escape") {
        this.enabled = true;
        switch (mode) {
          case "grid":
            this.setFocusedCol(focusedRow, focusedCol);
            break;
          case "list":
            this.setFocusedRow(focusedRow);
            break;
        }
      }
      if (focusedRow === __privateGet(this, _focusedRow) && focusedCol === __privateGet(this, _focusedCol)) {
        return;
      }
      mode === "grid" ? this.setFocusedCol(focusedRow, focusedCol) : this.setFocusedRow(focusedRow);
      e.preventDefault();
    };
    this.handleMouseOver = (e) => {
      const target = e.target;
      const row = closest2(target, ROW_SELECTORS);
      if (!row || row.dataset.row === void 0) {
        return;
      }
      const rowIndex = Number.parseInt(row.dataset.row);
      __privateSet(this, _highlightedRow, rowIndex);
      if (this.options.mode === "grid") {
        const cell = closest2(target, CELL_SELECTORS);
        if (!cell || cell.dataset.col === void 0) {
          return;
        }
        const colIndex = Number.parseInt(cell.dataset.col);
        if (colIndex === __privateGet(this, _highlightedCol)) {
          return;
        }
        __privateSet(this, _highlightedCol, colIndex);
      }
    };
    this.setFocusedRow = (row) => {
      const el = this.gridEl?.querySelector(
        `[data-row="${row}"]`
      );
      el?.setAttribute("data-focused", "");
      el?.setAttribute("tabindex", "0");
      el?.focus();
      el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      if (__privateGet(this, _focusedElement) && __privateGet(this, _focusedElement) !== el) {
        __privateGet(this, _focusedElement).removeAttribute("data-focused");
        __privateGet(this, _focusedElement).setAttribute("tabindex", "-1");
      }
      __privateSet(this, _focusedElement, el);
      __privateSet(this, _focusedRow, row);
      this.options.onFocusChange?.({ row, column: 0 });
    };
    this.setFocusedCol = (row, col) => {
      const cell = this.gridEl?.querySelector(
        `[data-row="${row}"] > [data-col="${col}"]`
      );
      cell?.setAttribute("data-focused", "");
      cell?.setAttribute("tabindex", "0");
      cell?.focus();
      if (__privateGet(this, _focusedElement) && __privateGet(this, _focusedElement) !== cell) {
        __privateGet(this, _focusedElement).removeAttribute("data-focused");
        __privateGet(this, _focusedElement).setAttribute("tabindex", "-1");
      }
      __privateSet(this, _focusedElement, cell);
      this.enabled = true;
      __privateSet(this, _focusedRow, row);
      __privateSet(this, _focusedCol, col);
      this.options.onFocusChange?.({ row, column: col });
    };
    this.init();
  }
  init() {
    __privateGet(this, _debug).call(this, "init", this.gridEl, this.options);
    this.gridEl?.addEventListener("mousedown", this.handleMouseDown);
    this.gridEl?.addEventListener("keydown", this.handleKeyDown);
    this.gridEl?.addEventListener("mouseover", this.handleMouseOver);
  }
  isValidRow(row) {
    return !!this.gridEl?.querySelector(
      `[data-row="${row}"]`
    );
  }
  isValidCell(row, col) {
    return !!this.gridEl?.querySelector(
      `[data-row="${row}"] > [data-col="${col}"]`
    );
  }
  hasSelectedRows() {
    return this.options.getSelectedRows ? this.options.getSelectedRows().length : this.gridEl?.querySelector(SELECTED_ROW_SELECTORS) !== null;
  }
  selectRows(start, end) {
    this.options.onSelectRows?.(start, end);
  }
  get focusedRow() {
    return __privateGet(this, _focusedRow);
  }
  get focusedCol() {
    return __privateGet(this, _focusedCol);
  }
  reset() {
    __privateGet(this, _focusedElement)?.removeAttribute("data-focused");
    __privateGet(this, _focusedElement)?.setAttribute("tabindex", "-1");
    __privateSet(this, _focusedElement, null);
    __privateSet(this, _focusedRow, 0);
    __privateSet(this, _focusedCol, 0);
    __privateSet(this, _highlightedRow, null);
    __privateSet(this, _highlightedCol, null);
  }
  destroy() {
    this.gridEl?.removeEventListener("mousedown", this.handleMouseDown);
    this.gridEl?.removeEventListener("mouseover", this.handleMouseOver);
    this.gridEl?.removeEventListener("keydown", this.handleKeyDown);
  }
};
_focusedRow = new WeakMap();
_focusedCol = new WeakMap();
_focusedElement = new WeakMap();
_highlightedRow = new WeakMap();
_highlightedCol = new WeakMap();
_initialSelectedRow = new WeakMap();
_debug = new WeakMap();

// src/data-grid/focus-model/use-focus-model.ts
import React3 from "react";
var useFocusModel = (props) => {
  const { mode = "list", table, onFocusChange } = props;
  const tableRef = React3.useRef(null);
  const [focusModel, setFocusModel] = React3.useState(null);
  React3.useEffect(() => {
    if (!tableRef.current) {
      return;
    }
    const focusModel2 = new FocusModel(tableRef.current, {
      mode,
      onFocusChange: (state) => {
        if (onFocusChange) {
          const row = table.getRowModel().rows[state.row];
          const cell = row.getVisibleCells()[state.column];
          onFocusChange({ row, cell });
        }
      },
      getSelectedRows: () => {
        return Object.keys(table.getState().rowSelection);
      },
      onSelectRows: (start, end) => {
        const rows = table.getRowModel().rows;
        if (!end) {
          rows[start].toggleSelected(true);
          return;
        }
        const selectIds = rows.slice(start, end + 1).map((row) => row.id).filter(Boolean);
        table.setRowSelection(() => {
          const selections = {};
          for (const id of selectIds) {
            selections[id] = true;
          }
          return selections;
        });
      },
      onToggleRowSelected: (rowIndex) => {
        const rows = table.getRowModel().rows;
        const row = rows[rowIndex];
        if (row.getCanSelect()) {
          row.toggleSelected(!row.getIsSelected());
        }
      },
      onCollapseRow: (row) => {
        const rows = table.getRowModel().rows;
        rows[row]?.toggleExpanded(false);
      }
    });
    setFocusModel(focusModel2);
    return () => {
      focusModel2?.destroy();
    };
  }, [table]);
  const getRowProps = React3.useCallback(
    (row) => {
      const rows = table.getRowModel().rows;
      const rowIndex = rows.indexOf(row);
      if (mode === "grid") {
        return {
          "data-row": rowIndex
        };
      } else if (mode !== "list") {
        return;
      }
      return {
        ["data-row"]: rowIndex
      };
    },
    [mode, table]
  );
  const getCellProps = React3.useCallback(
    (cell) => {
      if (mode !== "grid") {
        return;
      }
      const visibleColumns = table.getVisibleFlatColumns();
      const columnIndex = visibleColumns.findIndex(
        (col) => col.id === cell.column.id
      );
      return {
        ["data-col"]: columnIndex
      };
    },
    [mode, table]
  );
  return {
    getRowProps,
    getCellProps,
    tableRef,
    setFocusedRow: focusModel?.setFocusedRow,
    setFocusedCell: focusModel?.setFocusedCol
  };
};

// src/data-grid/no-results.tsx
import { Button } from "@chakra-ui/react";
import {
  EmptyStateActions,
  EmptyStateContainer,
  EmptyStateDescription
} from "@saas-ui/react";
import { jsx as jsx16, jsxs as jsxs7 } from "react/jsx-runtime";
var NoResults = (props) => {
  const { state } = useDataGridContext();
  const count = state.columnFilters.length;
  const {
    resource = "results",
    title = state.globalFilter ? `No ${resource} found for "${state.globalFilter}"` : count ? `No ${resource} matching ${count} filters.` : `No ${resource}.`,
    clearLabel = "Clear filters",
    onReset,
    ...rest
  } = props;
  return /* @__PURE__ */ jsxs7(EmptyStateContainer, { variant: "no-results", ...rest, children: [
    /* @__PURE__ */ jsx16(EmptyStateDescription, { children: title }),
    !!state.columnFilters.length && /* @__PURE__ */ jsx16(EmptyStateActions, { children: /* @__PURE__ */ jsx16(Button, { onClick: onReset, variant: "ghost", size: "xs", children: clearLabel }) })
  ] });
};

// src/data-grid/data-grid.tsx
import { jsx as jsx17, jsxs as jsxs8 } from "react/jsx-runtime";
var DataGrid = React4.forwardRef(
  (props, ref) => {
    const {
      instanceRef,
      columns,
      data,
      initialState,
      getSubRows = (row) => row.subRows,
      defaultColumn,
      getRowId,
      isSortable,
      isSelectable,
      isHoverable = true,
      isExpandable,
      columnResizeMode = "onChange",
      columnResizeEnabled = false,
      onSelectedRowsChange,
      onSortChange,
      onFocusChange,
      onRowClick,
      onResetFilters,
      onScroll,
      emptyState: EmptyStateComponent = NoResults,
      noResults: NoResultsComponent = NoResults,
      pageCount,
      focusMode = "list",
      layoutMode = columnResizeEnabled ? "fixed" : "grow",
      colorScheme,
      size,
      variant,
      stickyHeader = true,
      className,
      sx,
      columnVirtualizerOptions,
      rowVirtualizerOptions,
      icons,
      slotProps,
      translations,
      children,
      ...rest
    } = props;
    const theme2 = useTheme();
    const styleConfig = theme2.components?.SuiDataGrid;
    const styles = useMultiStyleConfig("SuiDataGrid", {
      size,
      variant,
      colorScheme
    });
    const instance = useReactTable({
      columns: React4.useMemo(() => {
        const selectionColumn = columns?.[0]?.id === "selection" ? columns[0] : void 0;
        const expanderColumn = columns.find(({ id }) => id === "expand");
        return getSelectionColumn(isSelectable, selectionColumn).concat(getExpanderColumn(isExpandable, expanderColumn)).concat(
          columns?.filter(({ id }) => id !== "selection").map((column) => {
            if (!column.accessorKey && column.id) {
              column.accessorKey = column.id;
            }
            if (!column.cell) {
              column.cell = DataGridCellValue;
            }
            column.enableResizing = columnResizeEnabled ? column.enableResizing : false;
            return column;
          })
        );
      }, [columns, columnResizeEnabled]),
      data,
      initialState: React4.useMemo(() => initialState, []),
      defaultColumn,
      getSubRows,
      getRowId,
      manualPagination: pageCount !== void 0,
      pageCount,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getExpandedRowModel: getExpandedRowModel(),
      columnResizeMode,
      ...rest,
      _features: [CellSelectionFeature, ...rest._features ?? []]
    });
    const focusModel = useFocusModel({
      mode: focusMode,
      table: instance,
      onFocusChange
    });
    React4.useImperativeHandle(instanceRef, () => instance, [instanceRef]);
    const state = instance.getState();
    const rows = instance.getRowModel().rows;
    const scrollRef = React4.useRef(null);
    const columnVirtualizer = useColumnVirtualizer(instance, {
      enabled: false,
      getScrollElement: () => scrollRef.current,
      ...columnVirtualizerOptions
    });
    const _onSelectedRowsChange = useCallbackRef(onSelectedRowsChange);
    React4.useEffect(() => {
      _onSelectedRowsChange?.(Object.keys(state.rowSelection));
    }, [_onSelectedRowsChange, state.rowSelection, instance]);
    const _onSortChange = useCallbackRef(onSortChange);
    React4.useEffect(() => {
      _onSortChange?.(state.sorting);
    }, [_onSortChange, state.sorting]);
    const _onRowClick = useCallbackRef(onRowClick);
    const noResults = !rows.length && (state.columnFilters.length || state.globalFilter ? /* @__PURE__ */ jsx17(NoResultsComponent, { onReset: onResetFilters }) : /* @__PURE__ */ jsx17(EmptyStateComponent, {}));
    const innerStyles = {
      ...styles.inner,
      ...noResults ? { display: "flex", alignItems: "center" } : {}
    };
    const { columnSizing, columnSizingInfo, columnVisibility } = state;
    const columnSizeVars = React4.useMemo(() => {
      const headers = instance.getFlatHeaders();
      const colSizes = {
        "--column-grow": layoutMode === "grow" ? 1 : 0
      };
      for (let i = 0; i < headers.length; i++) {
        const header = headers[i];
        colSizes[`--header-${escapeId(header.id)}-size`] = header.getSize();
        colSizes[`--col-${escapeId(header.column.id)}-size`] = header.column.getSize();
      }
      return colSizes;
    }, [
      instance,
      columns,
      columnSizing,
      columnSizingInfo,
      columnVisibility,
      layoutMode
    ]);
    const expandedDepth = instance.getExpandedDepth();
    const expandedVars = isExpandable && expandedDepth ? {
      "--expanded-depth": expandedDepth
    } : {};
    const tableProps = runIfFn6(slotProps?.table, { table: instance });
    const table = /* @__PURE__ */ jsxs8(
      Table,
      {
        ref: useMergeRefs(ref, focusModel.tableRef, instance.setRootNode),
        ...tableProps,
        className: cx2("sui-data-grid", tableProps?.className),
        styleConfig,
        colorScheme,
        size,
        variant,
        sx,
        style: {
          ...columnSizeVars,
          ...expandedVars,
          ...tableProps?.style
        },
        children: [
          /* @__PURE__ */ jsx17(
            DataGridHeader,
            {
              instance,
              columnVirtualizer,
              stickyHeader,
              slotProps,
              isSortable
            }
          ),
          /* @__PURE__ */ jsx17(
            DataGridBody,
            {
              instance,
              scrollRef,
              size: size ?? styleConfig?.defaultProps?.size,
              isHoverable,
              isExpandable,
              isSelectable,
              slotProps,
              columnVirtualizer,
              focusModel,
              onRowClick: _onRowClick,
              rowVirtualizerOptions
            }
          ),
          /* @__PURE__ */ jsx17(
            DataGridFooter,
            {
              instance,
              slotProps,
              columnVirtualizer
            }
          )
        ]
      }
    );
    const containerProps = runIfFn6(slotProps?.container, { table: instance });
    const innerProps = runIfFn6(slotProps?.inner, { table: instance });
    return /* @__PURE__ */ jsx17(
      DataGridProvider,
      {
        instance,
        slotProps,
        colorScheme,
        variant,
        size,
        icons,
        translations,
        children: /* @__PURE__ */ jsxs8(
          chakra7.div,
          {
            ...containerProps,
            className: cx2("sui-data-grid", className, containerProps?.className),
            __css: styles.container,
            children: [
              /* @__PURE__ */ jsx17(
                chakra7.div,
                {
                  ...innerProps,
                  ref: scrollRef,
                  className: cx2("sui-data-grid__inner", innerProps?.className),
                  __css: innerStyles,
                  onScroll,
                  children: noResults || table
                }
              ),
              children
            ]
          }
        )
      }
    );
  }
);
DataGrid.displayName = "DataGrid";

// src/data-grid/data-grid-pagination.tsx
import * as React5 from "react";
import {
  IconButton as IconButton2,
  chakra as chakra8,
  useMultiStyleConfig as useMultiStyleConfig2
} from "@chakra-ui/react";
import { ButtonGroup, FormControl, FormLabel, Input } from "@chakra-ui/react";
import { callAllHandlers as callAllHandlers2, cx as cx3 } from "@chakra-ui/utils";

// src/utils/format-message.ts
var formatMessage = (message, values) => {
  return message.replace(/{([^}]+)}/g, (match, key) => {
    return String(values[key] ?? match);
  });
};

// src/data-grid/data-grid-pagination.tsx
import { Fragment as Fragment3, jsx as jsx18, jsxs as jsxs9 } from "react/jsx-runtime";
var DataGridPagination = (props) => {
  const { className, onChange, children, size = "sm", variant, ...rest } = props;
  const { instance } = useDataGridContext();
  const state = instance.getState();
  const {
    pagination: { pageIndex, pageSize }
  } = state;
  const styles = useMultiStyleConfig2("SuiDataGridPagination", {
    size
  });
  React5.useEffect(() => {
    onChange?.({ pageIndex, pageSize });
  }, [pageIndex, pageSize]);
  const content = children ?? /* @__PURE__ */ jsxs9(Fragment3, { children: [
    /* @__PURE__ */ jsx18(DataGridPaginationPageControl, { size }),
    /* @__PURE__ */ jsxs9(ButtonGroup, { ms: "2", size, variant, children: [
      /* @__PURE__ */ jsx18(DataGridPaginationPreviousButton, {}),
      /* @__PURE__ */ jsx18(DataGridPaginationNextButton, {})
    ] })
  ] });
  return /* @__PURE__ */ jsx18(
    chakra8.div,
    {
      className: cx3("sui-data-grid__pagination", className),
      __css: styles.container,
      ...rest,
      children: content
    }
  );
};
var DataGridPaginationPageControl = (props) => {
  const { instance, translations } = useDataGridContext();
  const state = instance.getState();
  const pageCount = instance.getPageCount();
  const {
    pagination: { pageIndex }
  } = state;
  const handleChange = (e) => {
    const page = e.target.value ? Number(e.target.value) - 1 : 0;
    instance.setPageIndex(page);
  };
  return /* @__PURE__ */ jsxs9(FormControl, { display: "flex", flexDirection: "row", alignItems: "center", children: [
    /* @__PURE__ */ jsx18(FormLabel, { mb: "0", children: translations.page }),
    /* @__PURE__ */ jsx18(
      Input,
      {
        type: "number",
        value: props.value ?? pageIndex + 1,
        onChange: callAllHandlers2(props.onChange, handleChange),
        onFocus: (e) => e.target.select(),
        w: "20",
        ...props,
        isDisabled: props.isDisabled ?? pageCount === 0
      }
    ),
    /* @__PURE__ */ jsx18(chakra8.span, { ms: "2", children: formatMessage(translations.of, { pageCount }) })
  ] });
};
var DataGridPaginationNextButton = (props) => {
  const { instance, translations } = useDataGridContext();
  const { nextPage } = instance;
  const icons = useDataGridIcons();
  const icon = props.icon ?? icons?.nextPage ?? /* @__PURE__ */ jsx18(ChevronRightIcon, {});
  return /* @__PURE__ */ jsx18(
    IconButton2,
    {
      ...props,
      onClick: callAllHandlers2(props.onClick, nextPage),
      isDisabled: !instance.getCanNextPage(),
      icon,
      "aria-label": translations.nextPage
    }
  );
};
var DataGridPaginationPreviousButton = (props) => {
  const { instance, translations } = useDataGridContext();
  const { previousPage } = instance;
  const icons = useDataGridIcons();
  const icon = props.icon ?? icons?.previousPage ?? /* @__PURE__ */ jsx18(ChevronLeftIcon, {});
  return /* @__PURE__ */ jsx18(
    IconButton2,
    {
      ...props,
      onClick: callAllHandlers2(props.onClick, previousPage),
      isDisabled: !instance.getCanPreviousPage(),
      icon,
      "aria-label": translations.previousPage
    }
  );
};

// src/data-grid/index.ts
import { createColumnHelper as createColumnHelper2 } from "@tanstack/react-table";

// src/data-grid/use-columns.ts
import React6 from "react";
import {
  createColumnHelper
} from "@tanstack/react-table";
var useColumns = (factory, deps) => React6.useMemo(() => {
  const columnHelper = createColumnHelper();
  return factory({
    ...columnHelper,
    actions: (column) => columnHelper.display({
      header: "",
      ...column,
      id: "_actions_",
      enableSorting: false,
      enableResizing: false,
      enableColumnFilter: false,
      enableGrouping: false,
      enableGlobalFilter: false,
      enableMultiSort: false
    })
  });
}, [...deps]);

// src/data-grid/use-column-visibility.ts
import React7 from "react";
var useColumnVisibility = (props, deps) => {
  const { columns, visibleColumns = [] } = props;
  const getVisibleColumns = React7.useCallback(
    (visibleColumns2) => {
      return columns.reduce((memo4, column) => {
        let id = column.id;
        if (!id && "accessorKey" in column && typeof column.accessorKey === "string") {
          id = column.accessorKey;
        }
        if (id) {
          memo4[id] = column.enableHiding !== false ? visibleColumns2?.includes(id) : true;
        }
        return memo4;
      }, {}) || {};
    },
    [columns]
  );
  const [columnVisibility, setColumnVisibility] = React7.useState(
    getVisibleColumns(visibleColumns)
  );
  React7.useEffect(
    () => {
      setColumnVisibility(getVisibleColumns(visibleColumns));
    },
    deps || [visibleColumns]
  );
  return columnVisibility;
};

// src/filters/filter-menu.tsx
import * as React11 from "react";
import {
  Button as Button2,
  Checkbox as Checkbox2,
  HStack,
  MenuButton,
  Portal,
  Spinner,
  forwardRef as forwardRef6,
  useControllableState as useControllableState2,
  useDisclosure
} from "@chakra-ui/react";
import {
  useVirtualizer as useVirtualizer2
} from "@tanstack/react-virtual";

// src/menu/responsive-menu.tsx
import * as React8 from "react";
import {
  Menu,
  MenuList,
  mergeRefs,
  useBreakpointValue
} from "@chakra-ui/react";
import { useRect } from "@radix-ui/react-use-rect";
import { MenuDialogList } from "@saas-ui/react";

// src/menu/responsive-menu-context.tsx
import { createContext } from "@chakra-ui/utils";
var [ResponseMenuContext, useResponsiveMenuContext] = createContext();

// src/menu/responsive-menu.tsx
import { jsx as jsx19 } from "react/jsx-runtime";
var ResponsiveMenu = (props) => {
  const { breakpoints = { base: true, md: false }, ...rest } = props;
  const isMobile = useBreakpointValue(breakpoints);
  return /* @__PURE__ */ jsx19(ResponseMenuContext, { value: { breakpoints }, children: /* @__PURE__ */ jsx19(Menu, { variant: isMobile ? "dialog" : "menu", ...rest }) });
};
var ResponsiveMenuList = React8.forwardRef((props, ref) => {
  const {
    children,
    title,
    closeOnOverlayClick,
    onOverlayClick,
    onEsc,
    useInert,
    hideCloseButton,
    initialFocusRef,
    ...rest
  } = props;
  const [list, setList] = React8.useState(null);
  const context = useResponsiveMenuContext();
  const isMobile = useBreakpointValue(context.breakpoints);
  const rect = useRect(list);
  const availableHeight = `calc(100dvh - ${rect?.top}px - 0.5rem)`;
  const style = {
    "--available-height": availableHeight,
    ...rest.style
  };
  if (isMobile) {
    const dialogProps = {
      title,
      closeOnOverlayClick,
      onOverlayClick,
      onEsc,
      useInert,
      hideCloseButton,
      initialFocusRef
    };
    return /* @__PURE__ */ jsx19(
      MenuDialogList,
      {
        ref: mergeRefs((el) => setList(el), ref),
        maxHeight: "var(--available-height)",
        ...dialogProps,
        ...rest,
        style,
        children
      }
    );
  }
  return /* @__PURE__ */ jsx19(
    MenuList,
    {
      ref: mergeRefs((el) => setList(el), ref),
      maxHeight: "var(--available-height)",
      ...rest,
      style,
      children
    }
  );
});

// src/menu/menu-property.tsx
import {
  Property,
  PropertyLabel,
  PropertyList,
  PropertyValue
} from "@saas-ui/react";
import { jsx as jsx20, jsxs as jsxs10 } from "react/jsx-runtime";
var MenuPropertyList = PropertyList;
var MenuProperty = (props) => {
  const { orientation, label, value } = props;
  const isVertical = orientation === "vertical";
  const propertyStyles = {
    px: "0.8rem",
    py: "0.4rem",
    ...isVertical ? {
      flexDirection: "column",
      alignItems: "flex-start"
    } : {}
  };
  const labelStyles = {
    flex: 1,
    width: "auto",
    py: 0,
    ...isVertical ? {
      mb: 2
    } : { maxW: "80%" }
  };
  const valueStyles = {
    flex: 0,
    py: 0
  };
  return /* @__PURE__ */ jsxs10(Property, { sx: propertyStyles, children: [
    /* @__PURE__ */ jsx20(PropertyLabel, { sx: labelStyles, children: label }),
    /* @__PURE__ */ jsx20(PropertyValue, { sx: valueStyles, children: value })
  ] });
};

// src/menu/menu-input.tsx
import {
  Input as Input2,
  InputGroup,
  InputRightElement,
  MenuCommand,
  MenuIcon,
  chakra as chakra9,
  forwardRef as forwardRef5,
  mergeRefs as mergeRefs3,
  useMenuContext as useMenuContext2,
  useMenuDescendantsContext,
  useMultiStyleConfig as useMultiStyleConfig3
} from "@chakra-ui/react";
import { cx as cx4 } from "@chakra-ui/utils";

// src/menu/use-menu-filter-item.ts
import * as React9 from "react";
import {
  mergeRefs as mergeRefs2,
  useMenuContext,
  useMenuDescendant
} from "@chakra-ui/react";
import { useClickable } from "@chakra-ui/react/clickable";
import { dataAttr as dataAttr5, getOwnerDocument } from "@chakra-ui/utils";
function isActiveElement(element) {
  const doc = getOwnerDocument(element);
  return doc.activeElement === element;
}
function isHTMLElement(el) {
  if (!isElement(el)) return false;
  const win = el.ownerDocument.defaultView ?? window;
  return el instanceof win.HTMLElement;
}
function isElement(el) {
  return el != null && typeof el == "object" && "nodeType" in el && el.nodeType === Node.ELEMENT_NODE;
}
function isTargetMenuItem(target) {
  return isHTMLElement(target) && !!target.getAttribute("role")?.startsWith("menuitem");
}
function useMenuFilterItem(props = {}, externalRef = null) {
  const {
    onMouseEnter: onMouseEnterProp,
    onMouseMove: onMouseMoveProp,
    onMouseLeave: onMouseLeaveProp,
    onClick: onClickProp,
    isDisabled,
    isFocusable,
    closeOnSelect,
    ...htmlProps
  } = props;
  const menu = useMenuContext();
  const {
    setFocusedIndex,
    focusedIndex,
    closeOnSelect: menuCloseOnSelect,
    onClose,
    menuId
  } = menu;
  const ref = React9.useRef(null);
  const id = `${menuId}-menuitem-${React9.useId()}`;
  const { index, register } = useMenuDescendant({
    disabled: isDisabled && !isFocusable
  });
  const onMouseEnter = React9.useCallback(
    (event) => {
      onMouseEnterProp?.(event);
      if (isDisabled) return;
      setFocusedIndex(index);
    },
    [setFocusedIndex, index, isDisabled, onMouseEnterProp]
  );
  const onMouseMove = React9.useCallback(
    (event) => {
      onMouseMoveProp?.(event);
      if (ref.current && !isActiveElement(ref.current)) {
        onMouseEnter(event);
      }
    },
    [onMouseEnter, onMouseMoveProp]
  );
  const onMouseLeave = React9.useCallback(
    (event) => {
      onMouseLeaveProp?.(event);
      if (isDisabled) return;
      setFocusedIndex(-1);
    },
    [setFocusedIndex, isDisabled, onMouseLeaveProp]
  );
  const onClick = React9.useCallback(
    (event) => {
      onClickProp?.(event);
      if (!isTargetMenuItem(event.currentTarget)) return;
      if (closeOnSelect ?? menuCloseOnSelect) {
        onClose();
      }
    },
    [onClose, onClickProp, menuCloseOnSelect, closeOnSelect]
  );
  const isFocused = index === focusedIndex;
  const clickableProps = useClickable({
    onClick,
    onMouseEnter,
    onMouseMove,
    onMouseLeave,
    ref: mergeRefs2(register, ref, externalRef),
    isDisabled,
    isFocusable
  });
  return {
    ...htmlProps,
    ...clickableProps,
    id,
    role: "menuitem",
    tabIndex: isFocused ? 0 : -1,
    "data-focus": dataAttr5(isFocused)
  };
}

// src/menu/menu-input.tsx
import { jsx as jsx21, jsxs as jsxs11 } from "react/jsx-runtime";
var navigationKeys = ["ArrowUp", "ArrowDown", "Escape"];
var MenuInput = forwardRef5(
  (props, forwardRef15) => {
    const { placeholder, command, groupProps, ...rest } = props;
    const { focusedIndex } = useMenuContext2();
    const { role, ref, contentEditable, ...inputProps } = useMenuFilterItem(rest);
    const descendants = useMenuDescendantsContext();
    return /* @__PURE__ */ jsxs11(
      InputGroup,
      {
        role,
        className: cx4("sui-menu-list__input"),
        borderBottomWidth: "1px",
        borderBottomColor: "chakra-border-color",
        position: "sticky",
        top: "0",
        ...groupProps,
        children: [
          /* @__PURE__ */ jsx21(
            Input2,
            {
              type: "text",
              position: "sticky",
              placeholder,
              borderBottomRadius: "0",
              borderWidth: "0",
              bg: "var(--menu-bg)",
              _dark: {
                bg: "var(--menu-bg)"
              },
              _hover: {
                outline: "none",
                boxShadow: "none"
              },
              _focus: {
                outline: "none",
                boxShadow: "none"
              },
              _focusVisible: {
                outline: "none",
                boxShadow: "none"
              },
              ref: mergeRefs3(forwardRef15, ref),
              ...inputProps,
              onKeyDown: (event) => {
                const eventKey = event.key;
                const keyMap = {
                  Enter: () => {
                    const item = descendants.item(focusedIndex);
                    item?.node?.click();
                  }
                };
                const action = keyMap[eventKey];
                if (action) {
                  event.preventDefault();
                  event.stopPropagation();
                  action(event);
                }
                if (!navigationKeys.includes(event.key)) {
                  event.stopPropagation();
                }
              }
            }
          ),
          command && /* @__PURE__ */ jsx21(InputRightElement, { children: /* @__PURE__ */ jsx21(MenuCommand, { children: command }) })
        ]
      }
    );
  }
);
var StyledMenuItem = forwardRef5(
  (props, ref) => {
    const { type, ...rest } = props;
    const styles = useMultiStyleConfig3("Menu");
    const btnType = rest.as ? type ?? void 0 : "button";
    const buttonStyles = {
      textDecoration: "none",
      color: "inherit",
      userSelect: "none",
      display: "flex",
      width: "100%",
      alignItems: "center",
      textAlign: "start",
      flex: "0 0 auto",
      outline: 0,
      ...styles.item
    };
    return /* @__PURE__ */ jsx21(chakra9.button, { ref, type: btnType, ...rest, __css: buttonStyles });
  }
);
var MenuFilterItem = forwardRef5(
  (props, ref) => {
    const {
      icon,
      iconSpacing = "0.75rem",
      command,
      commandSpacing = "0.75rem",
      children,
      ...rest
    } = props;
    const menuitemProps = useMenuFilterItem(
      rest,
      ref
    );
    const shouldWrap = icon || command;
    const _children = shouldWrap ? /* @__PURE__ */ jsx21("span", { style: { pointerEvents: "none", flex: 1 }, children }) : children;
    return /* @__PURE__ */ jsxs11(
      StyledMenuItem,
      {
        ...menuitemProps,
        className: cx4("chakra-menu__menuitem", menuitemProps.className),
        children: [
          icon && /* @__PURE__ */ jsx21(MenuIcon, { fontSize: "0.8em", marginEnd: iconSpacing, children: icon }),
          _children,
          command && /* @__PURE__ */ jsx21(MenuCommand, { marginStart: commandSpacing, children: command })
        ]
      }
    );
  }
);
MenuFilterItem.displayName = "MenuFilterItem";

// src/utils/split-props.ts
var splitProps = (props, keys) => {
  const picked = {};
  const omitted = { ...props };
  if (typeof keys === "function") {
    for (const key in props) {
      if (keys(key)) {
        picked[key] = props[key];
        delete omitted[key];
      }
    }
  } else {
    for (const key of keys) {
      picked[key] = props[key];
      delete omitted[key];
    }
  }
  return [picked, omitted];
};
var createSplitProps = (keys) => {
  return function split(props) {
    return splitProps(props, keys);
  };
};

// src/filters/use-active-filter.tsx
import * as React10 from "react";
import {
  useControllableState
} from "@chakra-ui/react";
import { callAllHandlers as callAllHandlers3, createContext as createContext2 } from "@chakra-ui/utils";
import { format, formatDistanceToNowStrict, isAfter } from "date-fns";
var [ActiveFilterProvider, useActiveFilterContext] = createContext2({
  name: "ActiveFilterContext",
  strict: false
});
var useActiveFilter = (props) => {
  const {
    id,
    defaultValue,
    defaultOperator,
    onChange,
    onOperatorChange: onOperatorChangeProp,
    onValueChange: onValueChangeProp
  } = props;
  const [filter, setFilter] = useControllableState({
    defaultValue: {
      id,
      value: defaultValue,
      operator: defaultOperator ?? "is"
    },
    onChange
  });
  const onOperatorChange = (operator) => {
    setFilter({
      ...filter,
      operator
    });
  };
  const onValueChange = (value) => {
    setFilter({
      ...filter,
      value
    });
  };
  return {
    filter,
    onOperatorChange: callAllHandlers3(onOperatorChange, onOperatorChangeProp),
    onValueChange: callAllHandlers3(onValueChange, onValueChangeProp)
  };
};
var useFilterOperator = (props) => {
  const {
    items,
    defaultValue,
    value: valueProp,
    onChange: onChangeProp
  } = props;
  const [value, setValue] = useControllableState({
    defaultValue: defaultValue || "is",
    value: valueProp,
    onChange: onChangeProp
  });
  const item = React10.useMemo(
    () => items?.find(({ id }) => id === value),
    [items, value]
  );
  const getItemProps = React10.useCallback(
    (item2) => {
      return {
        onClick: () => setValue(item2.id)
      };
    },
    [setValue]
  );
  return {
    value,
    label: item?.label || value,
    getItemProps
  };
};
var defaultFormatter = (value) => {
  if (value instanceof Date) {
    if (isAfter(value, /* @__PURE__ */ new Date())) {
      return format(value, "PP");
    }
    return formatDistanceToNowStrict(value, { addSuffix: true });
  }
  return value?.toString();
};
var useFilterValue = (props = {}) => {
  const filter = useActiveFilterContext();
  const {
    onChange: onChangeProp,
    value: valueProp,
    defaultValue,
    format: format2,
    items: itemsProp,
    multiple,
    children
  } = props;
  const [value, setValue] = useControllableState({
    defaultValue: defaultValue || "",
    value: valueProp,
    onChange: (value2) => {
      onChangeProp?.(value2);
    }
  });
  const {
    data: items,
    isLoading,
    isFetched
  } = useFilterItems({
    id: filter.id,
    value,
    items: React10.useMemo(() => props.items || [], [props.items])
  });
  const item = items?.find(({ id }) => id === value);
  const onChange = React10.useCallback(
    async (value2) => {
      setValue(value2);
    },
    [value, setValue]
  );
  const label = format2?.(value) || item?.label || defaultFormatter(value);
  const getMenuProps = React10.useCallback(() => {
    return {
      value,
      items: itemsProp || items,
      label: children || label,
      placeholder: filter.label,
      icon: item?.icon,
      multiple,
      onChange
    };
  }, [filter, value, label, item, itemsProp, multiple, onChange]);
  return {
    item,
    value,
    label,
    getMenuProps,
    isLoading,
    isFetched
  };
};

// src/filters/filter-menu.tsx
import { jsx as jsx22, jsxs as jsxs12 } from "react/jsx-runtime";
var itemCache = /* @__PURE__ */ new Map();
var useFilterItems = ({
  id,
  value,
  items,
  inputValue
}) => {
  const [data, setData] = React11.useState(
    itemCache.get(id) || []
  );
  const [isLoading, setLoading] = React11.useState(false);
  const [isFetched, setFetched] = React11.useState(false);
  const getItems = React11.useCallback(
    async (inputValue2 = "") => {
      if (typeof items === "function") {
        setLoading(true);
        const result = await items({
          query: inputValue2,
          id,
          value
        });
        setLoading(false);
        setFetched(true);
        return result;
      }
      return items;
    },
    [items, id, value]
  );
  React11.useEffect(() => {
    if (typeof items !== "function") {
      return;
    }
    const cacheItems = itemCache.get(id);
    if (cacheItems) {
      setFetched(true);
      setData(cacheItems);
    }
    getItems(inputValue).then((data2) => {
      setData(data2);
      const mergedData = [...cacheItems || [], ...data2].reduce(
        (acc, item) => {
          const exists = acc.some((existing) => existing.id === item.id);
          if (!exists) {
            acc.push(item);
          }
          return acc;
        },
        []
      );
      itemCache.set(id, mergedData);
    });
  }, [id, items, getItems, inputValue]);
  if (typeof items !== "function") {
    return { data: items, isLoading: false, isFetched: true };
  }
  return { data, isLoading, isFetched };
};
var FilterMenu = forwardRef6(
  (props, forwardedRef) => {
    const {
      items,
      label = "Filter",
      placeholder = "Filter...",
      command,
      icon,
      buttonProps,
      listProps,
      onSelect,
      value: valueProp,
      onChange: onChangeProp,
      isOpen: isOpenProp,
      defaultIsOpen,
      onOpen: onOpenProp,
      onClose: onCloseProp,
      inputValue,
      inputDefaultValue,
      onInputChange,
      multiple,
      closeOnSelect,
      virtualizer: virtualizerOptions,
      portalled,
      ...rest
    } = props;
    const listRef = React11.useRef(null);
    const [value, setValue] = useControllableState2({
      value: valueProp,
      onChange: (value2) => {
        onChangeProp?.(value2);
        if (!isOpen || !value2) {
          return;
        }
        if (activeItemRef.current) {
          onSelect?.({
            ...activeItemRef.current,
            value: value2
          });
          return;
        }
        let id = null;
        if (Array.isArray(value2)) {
          id = value2[0];
        } else if (typeof value2 === "string") {
          id = value2;
        }
        const filter = results?.find(
          (filter2) => filter2.id === id || filter2.value === id || filter2.value === value2
        );
        if (filter) {
          onSelect?.(filter);
        }
      }
    });
    const onCheck = React11.useCallback(
      (id, isChecked2) => {
        setValue((value2) => {
          let values = [];
          if (typeof value2 === "string") {
            values = [value2];
          } else if (Array.isArray(value2)) {
            values = value2.concat();
          }
          if (isChecked2 && values.indexOf(id) === -1) {
            values.push(id);
          } else if (!isChecked2) {
            values = values.filter((value3) => value3 !== id);
          }
          return values;
        });
      },
      [setValue]
    );
    const isChecked = (id) => {
      return Array.isArray(value) && value?.includes(id);
    };
    const { isOpen, onOpen, onClose } = useDisclosure({
      isOpen: isOpenProp,
      defaultIsOpen,
      onOpen() {
        onOpenProp?.();
        if (!isOpen) {
          setActiveItem(null);
          if (!props.value) {
            setValue(void 0);
          }
        }
        filterRef.current?.focus();
      },
      onClose() {
        onReset();
        onCloseProp?.();
      }
    });
    const filterRef = React11.useRef(null);
    const [activeItem, _setActiveItem] = React11.useState(null);
    const activeItemRef = React11.useRef(null);
    const setActiveItem = (item) => {
      activeItemRef.current = item || null;
      _setActiveItem(item);
    };
    const [filterValue, setFilterValue] = React11.useState(inputValue || "");
    const activeFilterContext = useActiveFilterContext();
    const { data, isLoading, isFetched } = useFilterItems({
      id: activeItem?.id ?? activeFilterContext?.id ?? "default",
      items: activeItem?.items || items,
      value,
      inputValue: filterValue
    });
    const { results, onReset, ...inputProps } = useSearchQuery({
      items: isLoading && !isFetched ? [] : data,
      fields: ["id", "label"],
      value: inputValue,
      defaultValue: inputDefaultValue,
      onChange: (value2) => {
        onInputChange?.(value2, activeItem?.id);
        setFilterValue(value2);
      }
    });
    const spinner = isLoading ? /* @__PURE__ */ jsx22(Spinner, { size: "sm", position: "absolute", top: "3", right: "3" }) : null;
    const onItemClick = React11.useCallback(
      async (item, close = true) => {
        const count = item.items?.length || 0;
        if (count > 1 || typeof item.items === "function") {
          setActiveItem(item);
          onReset();
          filterRef.current?.focus();
          return;
        } else if (count === 1) {
          setActiveItem(item);
          const value2 = item.items?.[0].value || item.items?.[0].id;
          setValue(value2);
        } else {
          const itemValue = item.value || item.id;
          const isMulti = multiple || item.multiple || activeItem?.multiple;
          const values = Array.isArray(value) ? value : [];
          isMulti ? onCheck(itemValue, !values.includes(itemValue)) : setValue(itemValue);
        }
        if (close) {
          onClose();
        }
      },
      [onReset, onClose, onSelect, activeItem, value, onCheck]
    );
    const input = /* @__PURE__ */ jsx22(
      MenuInput,
      {
        placeholder: activeItem?.label || placeholder,
        ref: filterRef,
        command,
        ...inputProps
      }
    );
    const filteredItems = React11.useMemo(() => {
      const isMulti = multiple || activeItem?.multiple;
      return results?.map((item, i) => {
        const [filterProps, itemProps] = splitProps(item, [
          "id",
          "label",
          "activeLabel",
          "type",
          "items",
          "value",
          "operators",
          "defaultOperator",
          "multiple",
          "icon"
        ]);
        const { id, icon: icon2 } = filterProps;
        const _icon = isMulti ? /* @__PURE__ */ jsxs12(HStack, { children: [
          /* @__PURE__ */ jsx22(
            Checkbox2,
            {
              isChecked: isChecked(id),
              onChange: (e) => onCheck(id, e.target.checked)
            }
          ),
          icon2
        ] }) : icon2;
        return /* @__PURE__ */ jsx22(
          MenuFilterItem,
          {
            value: id,
            icon: _icon,
            ...itemProps,
            onClick: (e) => {
              if (e.target.closest(".chakra-checkbox")) {
                e.stopPropagation();
                return;
              }
              onItemClick(item);
            },
            children: item.label
          },
          `${id}-${i}`
        );
      }) || [];
    }, [results, activeItem, onItemClick, closeOnSelect]);
    const virtualizer = useVirtualizer2({
      count: filteredItems?.length || 0,
      estimateSize: () => 32,
      getScrollElement: () => {
        return listRef.current;
      },
      ...virtualizerOptions
    });
    const shouldVirtualize = virtualizerOptions?.enabled || data?.length > 20;
    const renderItems = shouldVirtualize ? virtualizer.getVirtualItems() : filteredItems;
    const virtualPadding = useVirtualizerPadding(
      shouldVirtualize ? virtualizer : null
    );
    const list = /* @__PURE__ */ jsxs12(
      ResponsiveMenuList,
      {
        ref: listRef,
        zIndex: "dropdown",
        pt: "0",
        overflow: "auto",
        initialFocusRef: filterRef,
        hideCloseButton: true,
        ...listProps,
        children: [
          input,
          spinner,
          virtualPadding?.top ? /* @__PURE__ */ jsx22("div", { style: { height: `${virtualPadding.top}px` } }) : null,
          renderItems.map((itemOrVirtualItem) => {
            const item = "index" in itemOrVirtualItem ? filteredItems[itemOrVirtualItem.index] : itemOrVirtualItem;
            return /* @__PURE__ */ jsx22(React11.Fragment, { children: item }, item.key);
          }),
          virtualPadding?.bottom ? /* @__PURE__ */ jsx22("div", { style: { height: `${virtualPadding.bottom}px` } }) : null
        ]
      }
    );
    return /* @__PURE__ */ jsxs12(
      ResponsiveMenu,
      {
        isOpen,
        onOpen,
        onClose,
        closeOnSelect: closeOnSelect ?? false,
        ...rest,
        children: [
          /* @__PURE__ */ jsx22(
            MenuButton,
            {
              as: Button2,
              leftIcon: icon,
              ...buttonProps,
              ref: forwardedRef,
              children: label
            }
          ),
          portalled ? /* @__PURE__ */ jsx22(Portal, { children: list }) : list
        ]
      }
    );
  }
);
function useVirtualizerPadding(virtualizer) {
  const virtualItems = virtualizer?.getVirtualItems();
  let top = 0;
  let bottom = 0;
  if (virtualizer && virtualItems?.length) {
    const totalSize = virtualizer?.getTotalSize();
    top = virtualItems.length > 0 ? virtualItems?.[0]?.start || 0 : 0;
    bottom = virtualItems.length > 0 ? totalSize - (virtualItems?.[virtualItems.length - 1]?.end || 0) : 0;
  }
  return {
    top,
    bottom
  };
}

// src/filters/active-filter.tsx
import * as React13 from "react";
import {
  Button as Button3,
  ButtonGroup as ButtonGroup2,
  IconButton as IconButton3,
  Input as Input3,
  MenuButton as MenuButton2,
  MenuItem,
  Portal as Portal2,
  Wrap,
  WrapItem,
  chakra as chakra10,
  createStylesContext,
  forwardRef as forwardRef7,
  useMultiStyleConfig as useMultiStyleConfig4,
  useStyleConfig as useStyleConfig2
} from "@chakra-ui/react";
import { cx as cx5 } from "@chakra-ui/utils";

// src/filters/provider.tsx
import * as React12 from "react";
import { createContext as createContext3 } from "@chakra-ui/utils";
import { useMap } from "@react-hookz/web";

// src/filters/operators.ts
import { isAfter as isAfter2, isBefore, parseISO } from "date-fns";
var defaultOperators = [
  {
    id: "is",
    label: "is",
    types: ["enum", "string", "number", "boolean", "date"],
    comparator(value, filterValue) {
      return value === filterValue;
    }
  },
  {
    id: "isNot",
    label: "is not",
    types: ["enum", "string", "number", "boolean", "date"],
    comparator(value, filterValue) {
      return value !== filterValue;
    }
  },
  {
    id: "contains",
    label: "contains",
    types: ["string"],
    comparator(value, filterValue) {
      if (Array.isArray(value)) {
        return value.some((v) => v.toLowerCase() === filterValue?.toLowerCase());
      }
      return !!(filterValue && value?.toLowerCase().includes(filterValue.toLowerCase()));
    }
  },
  {
    id: "containsNot",
    label: "does not contain",
    types: ["string"],
    comparator(value, filterValue) {
      if (Array.isArray(value)) {
        return !value.some(
          (v) => v.toLowerCase() === filterValue?.toLowerCase()
        );
      }
      return value !== void 0 && !!filterValue && !value?.toLowerCase().includes(filterValue.toLowerCase());
    }
  },
  {
    id: "lessThan",
    label: "less than",
    types: ["number"],
    comparator(value, filterValue) {
      return value !== void 0 && value < filterValue;
    }
  },
  {
    id: "moreThan",
    label: "more than",
    types: ["number"],
    comparator(value, filterValue) {
      return value !== void 0 && value > filterValue;
    }
  },
  {
    id: "before",
    label: "before",
    types: ["date", "datetime"],
    comparator(value, filterValue) {
      if (typeof value === "string") {
        value = parseISO(value);
      }
      if (typeof filterValue === "string") {
        filterValue = parseISO(filterValue);
      }
      return !!value && isBefore(value, filterValue);
    }
  },
  {
    id: "after",
    label: "after",
    types: ["date", "datetime"],
    comparator(value, filterValue) {
      if (typeof value === "string") {
        value = parseISO(value);
      }
      if (typeof filterValue === "string") {
        filterValue = parseISO(filterValue);
      }
      return !!value && isAfter2(value, filterValue);
    }
  }
];
var createOperators = (operators) => {
  return operators;
};

// src/filters/provider.tsx
import { jsx as jsx23 } from "react/jsx-runtime";
var [FiltersContextProvider, useFiltersContext] = createContext3({
  name: "FiltersContext"
});
var FiltersProvider = (props) => {
  const {
    children,
    filters,
    activeFilters: activeFiltersProp,
    defaultFilters,
    operators = defaultOperators,
    onChange,
    onBeforeEnableFilter
  } = props;
  const initializedRef = React12.useRef(false);
  const activeFilterMap = useMap([]);
  const getFilter = React12.useCallback(
    (id) => {
      return filters?.find((filter) => filter.id === id);
    },
    [filters]
  );
  const getActiveFilter = React12.useCallback(
    (id) => {
      return activeFilterMap.get(id);
    },
    [filters]
  );
  const getActiveFilters = React12.useCallback(
    () => Array.from(activeFilterMap.entries()).map(([key, filter]) => ({
      key,
      ...filter
    })),
    [activeFilterMap]
  );
  const getOperators = React12.useCallback(
    (type = "string") => {
      return operators.filter(({ types }) => types.includes(type));
    },
    [operators]
  );
  const _setFilter = React12.useCallback((filter) => {
    const key = filter.key || `${filter.id}-${activeFilterMap.size}`;
    const def = getFilter(filter.id);
    const operator = filter.operator || def?.defaultOperator || "is";
    activeFilterMap.set(key, {
      ...filter,
      operator
    });
  }, []);
  const enableFilter = React12.useCallback(
    async (filter) => {
      const key = filter.key || `${filter.id}-${activeFilterMap.size}`;
      const def = getFilter(filter.id);
      const operator = filter.operator || def?.defaultOperator || "is";
      const _enable = (key2, filter2) => {
        activeFilterMap.set(key2, filter2);
        onChange?.(getActiveFilters());
        return key2;
      };
      if (onBeforeEnableFilter) {
        const result = await onBeforeEnableFilter(
          {
            ...filter,
            operator,
            key
          },
          def
        );
        return _enable(result.key || key, result);
      }
      return _enable(key, {
        ...filter,
        operator
      });
    },
    [onBeforeEnableFilter, onChange]
  );
  const disableFilter = React12.useCallback(
    (key) => {
      activeFilterMap.delete(key);
      onChange?.(getActiveFilters());
    },
    [onChange]
  );
  const reset = React12.useCallback(() => {
    activeFilterMap.clear();
    onChange?.([]);
  }, [onChange]);
  const activeFilters = getActiveFilters();
  React12.useEffect(() => {
    if (!initializedRef.current) {
      defaultFilters?.forEach((filter) => {
        _setFilter(filter);
      });
      initializedRef.current = true;
    }
  }, [defaultFilters, enableFilter]);
  React12.useEffect(() => {
    if (activeFiltersProp) {
      activeFilterMap.clear();
      activeFiltersProp?.forEach(_setFilter);
    }
  }, [activeFiltersProp]);
  const context = {
    filters,
    activeFilters,
    enableFilter,
    disableFilter,
    getFilter,
    getActiveFilter,
    getOperators,
    reset
  };
  return /* @__PURE__ */ jsx23(FiltersContextProvider, { value: context, children });
};
var useFilters = () => {
  const context = useFiltersContext();
  return context.filters;
};

// src/filters/active-filter.tsx
import { jsx as jsx24, jsxs as jsxs13 } from "react/jsx-runtime";
var [StylesProvider, useStyles] = createStylesContext("SuiActiveFilter");
var splitActiveFilterProps = createSplitProps([
  "defaultOperator",
  "defaultValue",
  "formatLabel",
  "formatValue",
  "icon",
  "id",
  "items",
  "label",
  "multiple",
  "onChange",
  "onOperatorChange",
  "onRemove",
  "onValueChange",
  "operator",
  "operators",
  "renderValue",
  "type",
  "value"
]);
var ActiveFilter = (props) => {
  const [filterProps, containerProps] = splitActiveFilterProps(props);
  const {
    icon,
    label,
    value,
    defaultValue,
    items,
    operators,
    operator,
    defaultOperator,
    onRemove,
    formatLabel,
    formatValue,
    renderValue,
    multiple
  } = filterProps;
  const { filter, onOperatorChange, onValueChange } = useActiveFilter(filterProps);
  const context = {
    ...filter,
    items,
    value: defaultValue || value,
    label,
    onValueChange
  };
  return /* @__PURE__ */ jsx24(ActiveFilterProvider, { value: context, children: /* @__PURE__ */ jsxs13(ActiveFilterContainer, { ...containerProps, children: [
    /* @__PURE__ */ jsx24(ActiveFilterLabel, { icon, children: formatLabel?.(label) || label }),
    /* @__PURE__ */ jsx24(
      ActiveFilterOperator,
      {
        items: operators,
        value: operator,
        defaultValue: defaultOperator,
        onChange: onOperatorChange
      }
    ),
    /* @__PURE__ */ jsx24(
      ActiveFilterValue,
      {
        items,
        value,
        defaultValue,
        onChange: onValueChange,
        multiple,
        format: formatValue,
        children: renderValue?.(context)
      }
    ),
    /* @__PURE__ */ jsx24(
      ActiveFilterRemove,
      {
        onClick: onRemove,
        onKeyDown: (e) => {
          if (e.key === "Enter") {
            onRemove?.();
          }
        }
      }
    )
  ] }) });
};
ActiveFilter.displayName = "ActiveFilter";
var ActiveFilterValueInput = (props) => {
  const { value, onValueChange } = useActiveFilterContext();
  return /* @__PURE__ */ jsx24(
    Input3,
    {
      type: "text",
      value: value?.toString(),
      size: "sm",
      autoFocus: true,
      variant: "plain",
      width: "80px",
      px: "0",
      bg: "none",
      borderRadius: "0",
      placeholder: "Enter a value...",
      onChange: (e) => {
        onValueChange?.(e.target.value);
      },
      ...props
    }
  );
};
var ActiveFilterContainer = (props) => {
  const { children, ...rest } = props;
  const styles = useMultiStyleConfig4("SuiActiveFilter", props);
  const containerStyles = {
    borderWidth: "1px",
    borderRadius: "md",
    display: "flex",
    ...styles.container
  };
  return /* @__PURE__ */ jsx24(StylesProvider, { value: styles, children: /* @__PURE__ */ jsx24(
    ButtonGroup2,
    {
      variant: "ghost",
      isAttached: true,
      ...rest,
      sx: containerStyles,
      className: cx5("sui-active-filter", props.className),
      children
    }
  ) });
};
ActiveFilterContainer.displayName = "ActiveFilterContainer";
var ActiveFilterLabel = (props) => {
  const { children, icon, iconSpacing = 2, ...rest } = props;
  const styles = useStyles();
  const labelStyles = {
    display: "flex",
    alignItems: "center",
    px: 2,
    ...styles.label
  };
  const _icon = React13.isValidElement(icon) ? React13.cloneElement(icon, {
    "aria-hidden": true,
    focusable: false
  }) : null;
  return /* @__PURE__ */ jsxs13(
    chakra10.div,
    {
      ...rest,
      __css: labelStyles,
      className: cx5("sui-active-filter__label", props.className),
      children: [
        _icon && /* @__PURE__ */ jsx24(chakra10.span, { marginEnd: iconSpacing, display: "inline-flex", children: _icon }),
        /* @__PURE__ */ jsx24(chakra10.span, { children })
      ]
    }
  );
};
ActiveFilterLabel.displayName = "ActiveFilterLabel";
var ActiveFilterOperator = (props) => {
  const { items, buttonProps, menuListProps, portalled, portalProps, ...rest } = props;
  const styles = useStyles();
  const operatorStyles = {
    color: "muted",
    px: 2,
    minWidth: 0,
    ...styles.operator
  };
  const { label, getItemProps } = useFilterOperator(props);
  const list = /* @__PURE__ */ jsx24(ResponsiveMenuList, { zIndex: "dropdown", ...menuListProps, children: items?.map((item) => /* @__PURE__ */ jsx24(MenuItem, { icon: item.icon, ...getItemProps(item), children: item.label }, item.id)) });
  return /* @__PURE__ */ jsxs13(ResponsiveMenu, { ...rest, children: [
    /* @__PURE__ */ jsx24(
      MenuButton2,
      {
        as: ActiveFilterButton,
        sx: operatorStyles,
        ...buttonProps,
        className: cx5("sui-active-filter__operator"),
        children: label
      }
    ),
    portalled ? /* @__PURE__ */ jsx24(Portal2, { ...portalProps, children: list }) : list
  ] });
};
ActiveFilterOperator.displayName = "ActiveFilterOperator";
var ActiveFilterValue = (props) => {
  const { children, ...rest } = props;
  const [, htmlProps] = splitProps(rest, [
    "defaultValue",
    "items",
    "multiple",
    "onChange",
    "value"
  ]);
  const styles = useStyles();
  const valueStyles = {
    display: "flex",
    alignItems: "center",
    px: 2,
    ...styles.value
  };
  const { item, label, getMenuProps, isLoading, isFetched } = useFilterValue(props);
  const [, menuProps] = splitProps(getMenuProps(), ["icon"]);
  if (typeof menuProps.items === "function" || menuProps.items?.length) {
    return /* @__PURE__ */ jsx24(
      FilterMenu,
      {
        ...menuProps,
        buttonProps: {
          as: ActiveFilterButton,
          leftIcon: item?.icon,
          isLoading: isLoading && !isFetched
        }
      }
    );
  }
  return /* @__PURE__ */ jsx24(
    chakra10.div,
    {
      ...htmlProps,
      __css: valueStyles,
      className: cx5("sui-active-filter__value", props.className),
      children: children || label
    }
  );
};
ActiveFilterValue.displayName = "ActiveFilterValue";
var ActiveFilterRemove = (props) => {
  const styles = useStyles();
  const removeStyles = {
    px: 2,
    fontSize: "1.2em",
    ...styles.remove
  };
  return /* @__PURE__ */ jsx24(
    IconButton3,
    {
      icon: /* @__PURE__ */ jsx24(XIcon, {}),
      as: "div",
      "aria-label": "Remove filter",
      role: "button",
      tabIndex: 0,
      ...props,
      sx: removeStyles,
      className: cx5("sui-active-filter__remove", props.className)
    }
  );
};
ActiveFilterRemove.displayName = "ActiveFilterRemove";
var ActiveFilterButton = forwardRef7((props, ref) => {
  const styles = useStyles();
  const buttonStyles = {
    fontWeight: "normal",
    px: "2",
    rounded: "none",
    ...styles.valueButton
  };
  return /* @__PURE__ */ jsx24(
    Button3,
    {
      as: "div",
      role: "button",
      tabIndex: 0,
      ...props,
      sx: {
        ...buttonStyles,
        ...props.sx
      },
      ref
    }
  );
});
ActiveFilterButton.displayName = "ActiveFilterButton";
var ActiveFiltersList = (props) => {
  const { formatValue, renderValue, children, size, ...rest } = props;
  const {
    activeFilters,
    getOperators,
    getFilter,
    enableFilter,
    disableFilter
  } = useFiltersContext();
  const styles = useStyleConfig2("SuiActiveFiltersList", props);
  return activeFilters?.length ? /* @__PURE__ */ jsxs13(Wrap, { ...rest, __css: styles, children: [
    activeFilters?.map((activeFilter) => {
      const { key, id, value, operator } = activeFilter;
      const filter = getFilter(id);
      const operators = getOperators(filter?.type).filter(({ id: id2 }) => {
        if (filter?.operators && !filter.operators.includes(id2)) {
          return false;
        }
        return true;
      });
      const multiple = !!filter?.multiple;
      const activeFilterProps = {
        id,
        value,
        operator,
        multiple,
        icon: filter?.icon,
        label: filter?.activeLabel || filter?.label,
        defaultOperator: filter?.defaultOperator,
        items: filter?.items,
        operators,
        type: filter?.type,
        size,
        onValueChange: (value2) => {
          enableFilter({ key, ...activeFilter, value: value2 });
        },
        onOperatorChange: (operator2) => {
          enableFilter({ key, ...activeFilter, operator: operator2 });
        },
        onRemove: () => key && disableFilter(key),
        formatValue,
        renderValue
      };
      return /* @__PURE__ */ jsx24(WrapItem, { children: /* @__PURE__ */ jsx24(ActiveFilter, { ...activeFilterProps }) }, key);
    }),
    children
  ] }) : null;
};
var ResetFilters = (props) => {
  const { children, ...rest } = props;
  const { reset } = useFiltersContext();
  return /* @__PURE__ */ jsx24(Button3, { variant: "ghost", size: "sm", ...rest, onClick: reset, children });
};

// src/filters/use-data-grid-filter.ts
import React14 from "react";
function testFalsey(val) {
  return val === void 0 || val === null || val === "";
}
var getDataGridFilter = (type = "string", operators = defaultOperators) => {
  const typeOperators = operators.reduce(
    (memo4, operator) => {
      if (operator.types.includes(type)) {
        memo4[operator.id] = operator;
      }
      return memo4;
    },
    {}
  );
  const dataGridFilter = (row, columnId, filterValue) => {
    const { value, operator } = filterValue;
    const rowValue = row.getValue(columnId);
    return !!typeOperators[operator]?.comparator(rowValue, value);
  };
  dataGridFilter.autoRemove = (val) => testFalsey(val);
  return dataGridFilter;
};
var useDataGridFilter = (type = "string", operators = defaultOperators) => {
  return React14.useMemo(
    () => getDataGridFilter(type, operators),
    [type, operators]
  );
};

// src/filters/no-filtered-results.tsx
import { jsx as jsx25 } from "react/jsx-runtime";
var NoFilteredResults = (props) => {
  const { reset } = useFiltersContext();
  const { onReset: onResetProp, ...rest } = props;
  const onReset = () => {
    reset();
    onResetProp?.();
  };
  return /* @__PURE__ */ jsx25(NoResults, { onReset, ...rest });
};

// src/filters/filters.tsx
import * as React15 from "react";
import { forwardRef as forwardRef8 } from "@chakra-ui/react";
import { jsx as jsx26 } from "react/jsx-runtime";
var FiltersAddButton = forwardRef8((props, ref) => {
  const { onOpen: onOpenProp, onClose: onCloseProp, isOpen, ...rest } = props;
  const { filters, enableFilter } = useFiltersContext();
  const openRef = React15.useRef(false);
  const [currentKey, setCurrentKey] = React15.useState();
  const onSelect = async (item) => {
    const { id, value } = item;
    const key = await enableFilter(
      currentKey ? { key: currentKey, id, value } : { id, value }
    );
    if (openRef.current) {
      setCurrentKey(key);
    }
  };
  const onOpen = () => {
    setCurrentKey(void 0);
    onOpenProp?.();
    openRef.current = true;
  };
  const onClose = () => {
    setCurrentKey(void 0);
    onCloseProp?.();
    openRef.current = false;
  };
  return /* @__PURE__ */ jsx26(
    FilterMenu,
    {
      items: filters || [],
      icon: /* @__PURE__ */ jsx26(FilterIcon, {}),
      ref,
      onSelect,
      onOpen,
      onClose,
      isOpen,
      ...rest
    }
  );
});

// src/hooks/use-search-query.ts
import * as React16 from "react";
import { useControllableState as useControllableState3 } from "@chakra-ui/react";
var regExpSyntaxCharacter = /[.*+?^${}()|[\]\\]/g;
function escapeRegExp(value) {
  return value.replace(regExpSyntaxCharacter, "\\$&");
}
var useSearchQuery = (props = {}) => {
  const {
    items,
    fields = ["id"],
    defaultValue = "",
    value,
    onChange: onChangeProp
  } = props;
  const [query, setQuery] = useControllableState3({
    defaultValue,
    value,
    onChange: onChangeProp
  });
  const results = React16.useMemo(() => {
    if (!query || !query.length) {
      return items;
    }
    const re = query && new RegExp(escapeRegExp(query), "i");
    return items?.filter(
      (item) => fields.find((field) => item[field]?.match(re))
    );
  }, [query, items]);
  const onChange = ({ target }) => {
    setQuery(target.value);
  };
  const onReset = () => setQuery("");
  return { value: query, results, onReset, onChange };
};

// src/page/page.tsx
import {
  chakra as chakra11,
  createStylesContext as createStylesContext2,
  forwardRef as forwardRef9,
  omitThemingProps,
  useMultiStyleConfig as useMultiStyleConfig5
} from "@chakra-ui/react";
import { createContext as createContext4, cx as cx6 } from "@chakra-ui/utils";
import { ErrorBoundary, LoadingOverlay, LoadingSpinner } from "@saas-ui/react";

// src/page/error-page.tsx
import { EmptyState } from "@saas-ui/react";
import { jsx as jsx27 } from "react/jsx-runtime";
var ErrorPage = (props) => {
  return /* @__PURE__ */ jsx27(EmptyState, { flex: "1", ...props });
};

// src/page/page.tsx
import { jsx as jsx28, jsxs as jsxs14 } from "react/jsx-runtime";
var [StylesProvider2, useStyles2] = createStylesContext2("SuiPage");
var [PageProvider, usePageContext] = createContext4();
var PageTitle = forwardRef9(
  (props, ref) => {
    const styles = useStyles2();
    return /* @__PURE__ */ jsx28(chakra11.div, { ref, __css: styles.title, as: "h2", ...props });
  }
);
PageTitle.displayName = "PageTitle";
var PageDescription = forwardRef9(
  (props, ref) => {
    const styles = useStyles2();
    return /* @__PURE__ */ jsx28(chakra11.div, { ref, __css: styles.description, ...props });
  }
);
PageDescription.displayName = "PageDescription";
var PageHeader = forwardRef9(
  (props, ref) => {
    const { children, nav, title, description, toolbar, footer, ...rest } = props;
    const styles = useStyles2();
    let heading;
    if (title || description) {
      heading = /* @__PURE__ */ jsxs14(chakra11.div, { className: "sui-page__header-title", __css: styles.heading, children: [
        typeof title === "string" ? /* @__PURE__ */ jsx28(PageTitle, { children: title }) : title,
        typeof description === "string" ? /* @__PURE__ */ jsx28(PageDescription, { children: description }) : description
      ] });
    }
    let _footer;
    if (footer) {
      _footer = /* @__PURE__ */ jsx28(
        chakra11.div,
        {
          className: "sui-page__header-footer",
          __css: styles.headerFooter,
          children: footer
        }
      );
    }
    return /* @__PURE__ */ jsxs14(
      chakra11.header,
      {
        ref,
        __css: styles.headerContainer,
        ...rest,
        className: cx6("sui-page__header", props.className),
        children: [
          /* @__PURE__ */ jsxs14(chakra11.div, { __css: styles.header, className: "sui-page__header-content", children: [
            nav,
            heading,
            toolbar
          ] }),
          children,
          _footer
        ]
      }
    );
  }
);
PageHeader.displayName = "PageHeader";
var PageBody = forwardRef9((props, ref) => {
  const {
    contentWidth = "container.xl",
    children,
    contentProps,
    ...rest
  } = props;
  const styles = useStyles2();
  const { isLoading, skeleton, hasError, errorComponent } = usePageContext();
  let content = children;
  if (isLoading) {
    content = skeleton || /* @__PURE__ */ jsx28(LoadingOverlay, { children: /* @__PURE__ */ jsx28(LoadingSpinner, {}) });
  } else if (hasError) {
    content = errorComponent;
  }
  return /* @__PURE__ */ jsx28(
    chakra11.div,
    {
      ref,
      ...rest,
      __css: styles.body,
      className: cx6("sui-page__body", props.className),
      children: /* @__PURE__ */ jsx28(chakra11.div, { ...contentProps, maxW: contentWidth, children: content })
    }
  );
});
PageBody.displayName = "PageBody";
var PageContainer = forwardRef9(
  (props, ref) => {
    const {
      isLoading,
      errorComponent,
      skeleton,
      hasError,
      children,
      ...containerProps
    } = omitThemingProps(props);
    const styles = useMultiStyleConfig5("SuiPage", props);
    const context = {
      isLoading,
      errorComponent,
      skeleton,
      hasError
    };
    return /* @__PURE__ */ jsx28(PageProvider, { value: context, children: /* @__PURE__ */ jsx28(StylesProvider2, { value: styles, children: /* @__PURE__ */ jsx28(
      chakra11.main,
      {
        ref,
        ...containerProps,
        __css: styles.container,
        className: cx6("sui-page", props.className),
        children
      }
    ) }) });
  }
);
PageContainer.displayName = "PageContainer";
var Page = forwardRef9((props, ref) => {
  const {
    errorComponent = /* @__PURE__ */ jsx28(
      ErrorPage,
      {
        title: "Something went wrong",
        description: "We've been notified about the problem."
      }
    ),
    children,
    ...rest
  } = props;
  return /* @__PURE__ */ jsx28(ErrorBoundary, { fallback: errorComponent, children: /* @__PURE__ */ jsx28(PageContainer, { ref, errorComponent, ...rest, children }) });
});
Page.displayName = "Page";

// src/page/back-button.tsx
import { IconButton as IconButton4, forwardRef as forwardRef10 } from "@chakra-ui/react";
import { Link as Link2 } from "@saas-ui/react";

// src/page/icons.tsx
import { Fragment as Fragment5, jsx as jsx29, jsxs as jsxs15 } from "react/jsx-runtime";
var ArrowLeftIcon = createIcon({
  displayName: "ArrowLeftIcon",
  path: /* @__PURE__ */ jsxs15(Fragment5, { children: [
    /* @__PURE__ */ jsx29("line", { x1: "19", y1: "12", x2: "5", y2: "12" }),
    /* @__PURE__ */ jsx29("polyline", { points: "12 19 5 12 12 5" })
  ] })
});

// src/page/back-button.tsx
import { jsx as jsx30 } from "react/jsx-runtime";
var BackButton = forwardRef10(
  (props, ref) => {
    const { href, variant = "ghost", mr = 2, ...rest } = props;
    const icon = rest.icon || /* @__PURE__ */ jsx30(ArrowLeftIcon, {});
    let as;
    if (href) {
      as = Link2;
    }
    return /* @__PURE__ */ jsx30(
      IconButton4,
      {
        ref,
        "aria-label": "back",
        href,
        as,
        icon,
        variant,
        mr,
        ...rest
      }
    );
  }
);

// src/page/section.tsx
import {
  chakra as chakra12,
  createStylesContext as createStylesContext3,
  omitThemingProps as omitThemingProps2,
  useMultiStyleConfig as useMultiStyleConfig6
} from "@chakra-ui/react";
import { cx as cx7 } from "@chakra-ui/utils";
import { LoadingOverlay as LoadingOverlay2, LoadingSpinner as LoadingSpinner2 } from "@saas-ui/react";
import { jsx as jsx31, jsxs as jsxs16 } from "react/jsx-runtime";
var [StylesProvider3, useStyles3] = createStylesContext3("SuiSection");
var useSectionStyles = useStyles3;
var Section = (props) => {
  const { isLoading, children, ...rest } = props;
  let content;
  if (isLoading) {
    content = /* @__PURE__ */ jsx31(LoadingOverlay2, { p: 4, children: /* @__PURE__ */ jsx31(LoadingSpinner2, {}) });
  } else {
    content = children;
  }
  return /* @__PURE__ */ jsx31(SectionContainer, { ...rest, children: content });
};
Section.displayName = "Section";
var SectionBody = (props) => {
  const { children, ...rest } = props;
  const styles = useStyles3();
  const bodyStyles = {
    flex: 1,
    minWidth: 0,
    ...styles.body
  };
  return /* @__PURE__ */ jsx31(
    chakra12.div,
    {
      ...rest,
      __css: bodyStyles,
      className: cx7("sui-section__body", props.className),
      children
    }
  );
};
SectionBody.displayName = "SectionBody";
var SectionContainer = (props) => {
  const { children, variant, ...rest } = props;
  const styles = useMultiStyleConfig6("SuiSection", props);
  const containerProps = omitThemingProps2(rest);
  const containerStyles = {
    display: "flex",
    flexDirection: variant === "annotated" ? "row" : "column",
    maxWidth: "100%",
    ...styles.container
  };
  return /* @__PURE__ */ jsx31(StylesProvider3, { value: styles, children: /* @__PURE__ */ jsx31(
    chakra12.div,
    {
      ...containerProps,
      __css: containerStyles,
      className: cx7("sui-section", props.className),
      children
    }
  ) });
};
SectionContainer.displayName = "SectionContainer";
var SectionHeader = (props) => {
  const { title, description, children, ...rest } = props;
  const styles = useStyles3();
  const headerStyles = {
    flexShrink: 0,
    ...styles.header
  };
  return /* @__PURE__ */ jsxs16(
    chakra12.div,
    {
      ...rest,
      __css: headerStyles,
      className: cx7("sui-section__header", props.className),
      children: [
        typeof title === "string" ? /* @__PURE__ */ jsx31(SectionTitle, { children: title }) : title,
        typeof description === "string" ? /* @__PURE__ */ jsx31(SectionDescription, { children: description }) : description,
        children
      ]
    }
  );
};
SectionHeader.displayName = "SectionHeader";
var SectionTitle = (props) => {
  const styles = useStyles3();
  return /* @__PURE__ */ jsx31(chakra12.h3, { ...props, __css: styles.title });
};
SectionTitle.displayName = "SectionTitle";
var SectionDescription = (props) => {
  const styles = useStyles3();
  return /* @__PURE__ */ jsx31(chakra12.div, { ...props, __css: styles.description });
};
SectionDescription.displayName = "SectionDescription";

// src/page/split-page.tsx
import {
  chakra as chakra14,
  useBreakpointValue as useBreakpointValue2,
  useDisclosure as useDisclosure2,
  useMultiStyleConfig as useMultiStyleConfig7
} from "@chakra-ui/react";
import { createContext as createContext5 } from "@chakra-ui/utils";

// src/transitions/motion-box.tsx
import { chakra as chakra13 } from "@chakra-ui/react";
import { motion } from "framer-motion";
var MotionBox = chakra13(motion.div);

// src/page/split-page.tsx
import { jsx as jsx32, jsxs as jsxs17 } from "react/jsx-runtime";
var [SplitPageProvider, useSplitPage] = createContext5({
  strict: true,
  errorMessage: "SplitPage context not available."
});
var SplitPage = (props) => {
  const {
    children,
    defaultIsOpen,
    onClose,
    onOpen,
    isOpen,
    orientation,
    breakpoint = "lg",
    ...rest
  } = props;
  const styles = useMultiStyleConfig7("SuiSplitPage", props);
  const isMobile = useBreakpointValue2(
    {
      base: true,
      [breakpoint]: false
    },
    {
      fallback: breakpoint,
      ssr: typeof window === "undefined"
    }
  );
  const context = useDisclosure2({
    defaultIsOpen: defaultIsOpen || !isMobile,
    onClose,
    onOpen,
    isOpen
  });
  const containerStyles = {
    display: "flex",
    flexDirection: orientation === "vertical" ? "column" : "row",
    flex: 1,
    ...styles.container
  };
  const contentStyles = {
    ...styles.content,
    ...isMobile ? {
      position: "absolute",
      zIndex: "docked",
      top: 0,
      right: { base: "-100%", lg: "0" },
      bottom: 0,
      width: "100%"
    } : {}
  };
  const [startPage, endPage] = children;
  return /* @__PURE__ */ jsx32(SplitPageProvider, { value: context, children: /* @__PURE__ */ jsxs17(
    chakra14.main,
    {
      __css: containerStyles,
      className: "sui-split-page__container",
      ...rest,
      children: [
        startPage,
        /* @__PURE__ */ jsx32(
          MotionBox,
          {
            animate: context.isOpen ? "enter" : "exit",
            variants: {
              enter: {
                right: 0,
                opacity: 1,
                transition: { type: "spring", duration: 0.6, bounce: 0.15 }
              },
              exit: { right: "-100%", opacity: 0 }
            },
            __css: contentStyles,
            className: "sui-split-page__content",
            children: endPage
          }
        )
      ]
    }
  ) });
};

// src/page/aside.tsx
import {
  chakra as chakra17,
  createStylesContext as createStylesContext4,
  omitThemingProps as omitThemingProps3,
  useBreakpointValue as useBreakpointValue3,
  useMultiStyleConfig as useMultiStyleConfig8
} from "@chakra-ui/react";
import { cx as cx10 } from "@chakra-ui/utils";
import { ErrorBoundary as ErrorBoundary2, LoadingOverlay as LoadingOverlay3, LoadingSpinner as LoadingSpinner3 } from "@saas-ui/react";

// src/resize/resize-handle.tsx
import { chakra as chakra15 } from "@chakra-ui/react";
import { cx as cx8 } from "@chakra-ui/utils";

// src/resize/use-resize.ts
import * as React17 from "react";
import { useEventListener } from "@chakra-ui/react";
import { createContext as createContext6, dataAttr as dataAttr6 } from "@chakra-ui/utils";
var [ResizeProvider, useResizeContext] = createContext6({
  strict: false
});
var useResize = (props = {}) => {
  const {
    defaultWidth = 280,
    onResize,
    onHandleClick,
    isResizable = true,
    handlePosition = "right"
  } = props;
  const containerRef = React17.useRef(null);
  const timeoutRef = React17.useRef();
  const [isResizing, setIsResizing] = React17.useState(false);
  const [width, setWidth] = React17.useState(defaultWidth);
  const dimensionsRef = React17.useRef({ width });
  const limitsRef = React17.useRef({ minWidth: 0, maxWidth: void 0 });
  const stopResizing = React17.useCallback(() => {
    setIsResizing(false);
    onResize?.(dimensionsRef.current);
  }, []);
  const resize = React17.useCallback(
    (event) => {
      if (!isResizing || !containerRef.current) {
        return;
      }
      const r = containerRef.current.getBoundingClientRect();
      const { minWidth, maxWidth } = limitsRef.current;
      let w;
      if (handlePosition === "right") {
        w = event.clientX - (r?.left || 0);
      } else {
        w = r.right - event.clientX;
      }
      if (w < r.width && minWidth) {
        w = Math.max(w, minWidth);
      } else if (w > r.width && maxWidth) {
        w = Math.min(w, maxWidth);
      }
      setWidth(w);
      dimensionsRef.current = { width: r.width, height: r.height };
    },
    [isResizing]
  );
  React17.useEffect(() => {
    if (!containerRef.current || typeof getComputedStyle === "undefined") {
      return;
    }
    const styles = getComputedStyle(containerRef.current);
    limitsRef.current = {
      minWidth: parseInt(styles.getPropertyValue("min-width")),
      maxWidth: parseInt(styles.getPropertyValue("max-width")) || void 0
    };
  }, []);
  React17.useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", resize);
    }
    return () => {
      document.removeEventListener("mousemove", resize);
    };
  }, [isResizing]);
  useEventListener(() => document, "mouseup", stopResizing);
  const getContainerProps = React17.useCallback(
    () => ({
      ref: containerRef,
      ...isResizable ? {
        style: {
          width
        }
      } : {}
    }),
    [width, isResizable, containerRef]
  );
  const getHandleProps = React17.useCallback(
    () => ({
      onMouseDown: () => {
        timeoutRef.current = setTimeout(() => setIsResizing(true), 100);
      },
      onMouseUp: (e) => {
        if (!isResizing) {
          clearTimeout(timeoutRef.current);
          setIsResizing(false);
          onHandleClick?.(e);
        }
      },
      "data-resizing": dataAttr6(isResizing),
      [handlePosition]: "-5px"
    }),
    [handlePosition, isResizing]
  );
  return {
    getContainerProps,
    getHandleProps,
    width
  };
};

// src/resize/resize-handle.tsx
import { jsx as jsx33 } from "react/jsx-runtime";
var ResizeHandle = (props) => {
  const styles = {
    position: "absolute",
    userSelect: "none",
    width: "10px",
    height: "100%",
    top: "0px",
    cursor: "col-resize"
  };
  const context = useResizeContext();
  if (context?.isResizable === false) {
    return null;
  }
  return /* @__PURE__ */ jsx33(
    chakra15.div,
    {
      ...props,
      ...context?.getHandleProps(),
      __css: styles,
      className: cx8("sui-resize-handle", props.className)
    }
  );
};
ResizeHandle.displayName = "ResizeHandle";

// src/resize/resize-box.tsx
import { chakra as chakra16 } from "@chakra-ui/react";
import { cx as cx9 } from "@chakra-ui/utils";
import { jsx as jsx34, jsxs as jsxs18 } from "react/jsx-runtime";
var ResizeBox = (props) => {
  const {
    handlePosition,
    onResize,
    defaultWidth,
    isResizable = true,
    children,
    ...rest
  } = props;
  const { getContainerProps, getHandleProps } = useResize({
    handlePosition,
    onResize,
    defaultWidth,
    isResizable
  });
  return /* @__PURE__ */ jsxs18(
    chakra16.div,
    {
      ...rest,
      ...getContainerProps(),
      className: cx9("sui-resize-box", props.className),
      __css: { position: "relative" },
      children: [
        children,
        isResizable && /* @__PURE__ */ jsx34(ResizeHandle, { ...getHandleProps() })
      ]
    }
  );
};
ResizeBox.displayName = "ResizeBox";

// src/resize/resizer.tsx
import * as React18 from "react";
import { jsx as jsx35 } from "react/jsx-runtime";
var Resizer = (props) => {
  const {
    handlePosition,
    onResize,
    defaultWidth,
    isResizable = true,
    children
  } = props;
  const { getContainerProps, getHandleProps, width } = useResize({
    handlePosition,
    onResize,
    defaultWidth,
    isResizable
  });
  const context = {
    getContainerProps,
    getHandleProps,
    isResizable,
    handlePosition,
    width
  };
  let _child;
  if (React18.isValidElement(children)) {
    _child = React18.cloneElement(children, {
      ...children.props || {},
      ...getContainerProps()
    });
  }
  return /* @__PURE__ */ jsx35(ResizeProvider, { value: context, children: _child });
};
Resizer.displayName = "Resizer";

// src/page/aside.tsx
import { jsx as jsx36, jsxs as jsxs19 } from "react/jsx-runtime";
var [StylesProvider4, useStyles4] = createStylesContext4("SuiAside");
var AsideTitle = (props) => {
  const styles = useStyles4();
  return /* @__PURE__ */ jsx36(chakra17.div, { __css: styles.title, as: "h3", ...props });
};
AsideTitle.displayName = "AsideTitle";
var AsideHeader = (props) => {
  const { children, ...rest } = props;
  const styles = useStyles4();
  return /* @__PURE__ */ jsx36(
    chakra17.header,
    {
      __css: styles.header,
      ...rest,
      className: cx10("sui-aside__header", props.className),
      children
    }
  );
};
AsideHeader.displayName = "AsideHeader";
var AsideBody = (props) => {
  const { children, ...rest } = props;
  const styles = useStyles4();
  return /* @__PURE__ */ jsx36(
    chakra17.div,
    {
      ...rest,
      __css: styles.body,
      className: cx10("sui-aside__body", props.className),
      children
    }
  );
};
AsideBody.displayName = "AsideBody";
var AsideContainer = (props) => {
  const {
    children,
    isOpen,
    isResizable,
    onResize,
    defaultWidth,
    ...containerProps
  } = omitThemingProps3(props);
  const styles = useMultiStyleConfig8("SuiAside", props);
  const isMobile = useBreakpointValue3({ base: true, lg: false });
  const resize = useResize({
    defaultWidth,
    isResizable,
    handlePosition: "left",
    onResize
  });
  const innerStyles = {
    display: "flex",
    flexDirection: "column",
    height: "100%"
  };
  return /* @__PURE__ */ jsx36(StylesProvider4, { value: styles, children: /* @__PURE__ */ jsxs19(
    MotionBox,
    {
      animate: isOpen ? "enter" : "exit",
      variants: {
        enter: {
          ...isMobile ? { x: "0" } : { marginRight: 0 },
          transition: { type: "spring", duration: 0.6, bounce: 0.15 }
        },
        exit: isMobile ? { x: resize.width } : { marginRight: -resize.width }
      },
      __css: {
        ...styles.container
      },
      ...containerProps,
      className: cx10("sui-aside", props.className),
      ...resize.getContainerProps(),
      children: [
        /* @__PURE__ */ jsx36(chakra17.aside, { __css: innerStyles, children }),
        isResizable && /* @__PURE__ */ jsx36(ResizeHandle, { ...resize.getHandleProps() })
      ]
    }
  ) });
};
AsideContainer.displayName = "AsideContainer";
var Aside = (props) => {
  const { isLoading, skeleton, errorComponent, children, ...rest } = props;
  let content;
  if (isLoading) {
    content = skeleton || /* @__PURE__ */ jsx36(LoadingOverlay3, { children: /* @__PURE__ */ jsx36(LoadingSpinner3, {}) });
  } else {
    content = children;
  }
  return /* @__PURE__ */ jsx36(AsideContainer, { ...rest, children: /* @__PURE__ */ jsx36(ErrorBoundary2, { fallback: errorComponent, children: content }) });
};
Aside.displayName = "Aside";

// src/theme-tools/with-theme-colors.ts
import {
  mergeThemeOverride
} from "@chakra-ui/react";
import { memoizedGet as get, mergeWith } from "@chakra-ui/utils";
import { parseToHsl, setLightness, transparentize } from "polished";
var createGrayShades = (color, theme2) => {
  const shades = theme2.colors.gray;
  const gray = {};
  for (const shade in shades) {
    gray[shade] = setLightness(parseToHsl(shades[shade]).lightness, color);
  }
  return gray;
};
var transparentizeToken = (token, amount, theme2) => {
  if (typeof token === "string") {
    return transparentize(amount, get(theme2, `colors.${token}`, token));
  }
  return Object.fromEntries(
    Object.entries(token).map(([key, value]) => {
      return [key, transparentize(amount, value)];
    })
  );
};
var createColorScheme = ({ colors, tokens, grayTint }, theme2) => {
  const gray = grayTint && createGrayShades(grayTint, theme2);
  return {
    colors: {
      ...colors,
      gray
    },
    semanticTokens: {
      colors: mergeWith(
        {},
        tokens["sidebar-text"] ? {
          "sidebar-border-color": transparentizeToken(
            tokens["sidebar-text"],
            0.9,
            theme2
          ),
          "sidebar-muted": transparentizeToken(
            tokens["sidebar-text"],
            0.7,
            theme2
          ),
          "sidebar-on-muted": transparentizeToken(
            tokens["sidebar-text"],
            0.9,
            theme2
          ),
          "sidebar-on-subtle": transparentizeToken(
            tokens["sidebar-text"],
            0.8,
            theme2
          ),
          "sidebar-on": transparentizeToken(
            tokens["sidebar-text"],
            0.7,
            theme2
          )
        } : {},
        tokens
      )
    }
  };
};
var withThemeColors = (options) => {
  return (theme2) => {
    const colorScheme = createColorScheme(options, theme2);
    return mergeThemeOverride(theme2, colorScheme);
  };
};

// src/toolbar/toolbar.tsx
import {
  Button as Button4,
  ButtonGroup as ButtonGroup3,
  Divider,
  IconButton as IconButton5,
  Tooltip,
  chakra as chakra18,
  createStylesContext as createStylesContext5,
  forwardRef as forwardRef11,
  omitThemingProps as omitThemingProps4,
  useButtonGroup,
  useMenuOptionGroup,
  useMultiStyleConfig as useMultiStyleConfig9
} from "@chakra-ui/react";
import { cx as cx11 } from "@chakra-ui/utils";

// src/theme-tools/use-default-props.ts
import { useTheme as useTheme2 } from "@chakra-ui/react";
var useDefaultProps = (componentName) => {
  const theme2 = useTheme2();
  return theme2.components[componentName]?.defaultProps || {};
};

// src/toolbar/toolbar.tsx
import { jsx as jsx37 } from "react/jsx-runtime";
var [StylesProvider5, useStyles5] = createStylesContext5("SuiToolbar");
var Toolbar = forwardRef11((props, ref) => {
  const { children, className, variant, size, ...rest } = props;
  const styles = useMultiStyleConfig9("SuiToolbar", props);
  const defaultProps = useDefaultProps("SuiToolbar");
  const toolbarProps = omitThemingProps4(rest);
  const containerStyles = {
    display: "flex",
    flex: 1,
    justifyContent: "flex-end",
    ...styles.container
  };
  return /* @__PURE__ */ jsx37(StylesProvider5, { value: styles, children: /* @__PURE__ */ jsx37(
    chakra18.div,
    {
      as: ButtonGroup3,
      role: "toolbar",
      ...toolbarProps,
      variant: variant || defaultProps.variant,
      size: size || defaultProps.size,
      ref,
      __css: containerStyles,
      className: cx11("sui-toolbar", className),
      children
    }
  ) });
});
Toolbar.displayName = "Toolbar";
var ToolbarButton = forwardRef11(
  (props, ref) => {
    const { label, icon, tooltipProps, ...buttonProps } = props;
    let button;
    if (icon) {
      button = /* @__PURE__ */ jsx37(IconButton5, { ref, icon, ...buttonProps, "aria-label": label });
    } else {
      button = /* @__PURE__ */ jsx37(Button4, { ref, ...buttonProps, children: label });
    }
    if (icon || tooltipProps) {
      return /* @__PURE__ */ jsx37(
        Tooltip,
        {
          label,
          closeOnClick: false,
          openDelay: 400,
          ...tooltipProps,
          children: button
        }
      );
    }
    return button;
  }
);
var ToolbarDivider = (props) => {
  const styles = useStyles5();
  const dividerStyles = {
    flexShrink: 0,
    height: "100%",
    ...styles.divider
  };
  return /* @__PURE__ */ jsx37(
    chakra18.div,
    {
      ...props,
      __css: dividerStyles,
      className: cx11("sui-toolbar__divider", props.className),
      children: /* @__PURE__ */ jsx37(Divider, { orientation: "vertical" })
    }
  );
};
ToolbarDivider.displayName = "ToolbarDivider";
var ToolbarGroup = (props) => {
  const groupProps = useButtonGroup();
  return /* @__PURE__ */ jsx37(
    ButtonGroup3,
    {
      ...groupProps,
      ...props,
      className: cx11("sui-toolbar__group", props.className)
    }
  );
};
ToolbarGroup.displayName = "ToolbarGroup";
var ToolbarToggleGroup = (props) => {
  const groupProps = useMenuOptionGroup(props);
  const [, rest] = splitProps(props, ["onChange"]);
  return /* @__PURE__ */ jsx37(
    ToolbarGroup,
    {
      ...rest,
      ...groupProps,
      className: cx11("sui-toolbar__toggle-group", props.className)
    }
  );
};
ToolbarToggleGroup.displayName = "ToolbarToggleGroup";
var ToolbarToggleButton = forwardRef11((props, ref) => {
  const { isChecked, type, ...rest } = props;
  const { colorScheme, variant } = useButtonGroup();
  return /* @__PURE__ */ jsx37(
    ToolbarButton,
    {
      ref,
      role: type,
      "aria-checked": isChecked ? "true" : "false",
      colorScheme: isChecked ? "primary" : colorScheme,
      variant,
      ...rest,
      className: cx11("sui-toolbar__toggle-button", props.className)
    }
  );
});
ToolbarToggleButton.id = "MenuItemOption";
ToolbarToggleButton.displayName = "ToolbarToggleButton";

// src/utils/is-electron.ts
function isElectron() {
  if (typeof window !== "undefined" && typeof window.process === "object" && window.process.type === "renderer") {
    return true;
  }
  if (typeof process !== "undefined" && typeof process.versions === "object" && !!process.versions.electron) {
    return true;
  }
  if (typeof navigator === "object" && typeof navigator.userAgent === "string" && navigator.userAgent.indexOf("Electron") >= 0) {
    return true;
  }
  return false;
}

// src/utils/platform.ts
var platformSelect = (select, fallback, userAgent) => {
  let ua = userAgent;
  if (!ua && typeof window !== "undefined") {
    ua = window.navigator.userAgent;
  }
  if (!ua) return fallback;
  const platforms = {
    mac: /Mac/,
    windows: /Windows/,
    linux: /Linux|X11|OpenBSD/,
    android: /Android/,
    ipad: /iPad/,
    iphone: /iPhone/
  };
  let platform;
  for (const id in platforms) {
    if (platforms[id].test(ua)) {
      platform = id;
      break;
    }
  }
  if (platform && select[platform]) {
    return select[platform] || fallback;
  }
  return fallback;
};

// src/toggle/toggle-button-group.tsx
import * as React19 from "react";
import {
  Box,
  Button as Button5,
  ButtonGroup as ButtonGroup4,
  forwardRef as forwardRef12,
  useCheckbox
} from "@chakra-ui/react";
import { createField } from "@saas-ui/forms";

// src/toggle/use-toggle-group.ts
import { useCallback as useCallback8 } from "react";
import { useCallbackRef as useCallbackRef2, useControllableState as useControllableState4 } from "@chakra-ui/react";
import { isObject } from "@chakra-ui/utils";
function isInputEvent(value) {
  return value && isObject(value) && isObject(value.target);
}
function useToggleGroup(props = {}) {
  const {
    type = "checkbox",
    defaultValue = type === "checkbox" ? [] : void 0,
    value: valueProp,
    onChange,
    isDisabled,
    isNative
  } = props;
  const onChangeProp = useCallbackRef2(onChange);
  const [value, setValue] = useControllableState4(
    {
      value: valueProp,
      defaultValue,
      onChange: onChangeProp
    }
  );
  const isCheckbox = Array.isArray(value);
  const handleChange = useCallback8(
    (eventOrValue) => {
      const isChecked = isInputEvent(eventOrValue) ? eventOrValue.target.checked : isCheckbox ? !value.includes(eventOrValue) : value !== eventOrValue;
      const selectedValue = isInputEvent(eventOrValue) ? eventOrValue.target.value : eventOrValue;
      let nextValue;
      if (isCheckbox) {
        nextValue = isChecked ? [...value, selectedValue] : value.filter((v) => String(v) !== String(selectedValue));
      } else {
        nextValue = isChecked ? selectedValue : void 0;
      }
      setValue(nextValue);
    },
    [setValue, value]
  );
  const getToggleProps = useCallback8(
    (props2 = {}) => {
      const checkedKey = isNative ? "checked" : "isChecked";
      return {
        ...props2,
        [checkedKey]: isCheckbox ? value.some((val) => String(props2.value) === String(val)) : value === props2.value,
        onChange: handleChange
      };
    },
    [handleChange, isNative, value]
  );
  return {
    value,
    isDisabled,
    onChange: handleChange,
    setValue,
    getToggleProps
  };
}

// src/toggle/toggle-button-group.tsx
import { jsx as jsx38, jsxs as jsxs20 } from "react/jsx-runtime";
var ToggleButtonGroup = forwardRef12(
  (props, ref) => {
    const {
      children,
      type = "checkbox",
      defaultValue,
      value,
      onChange,
      isAttached = true,
      ...rest
    } = props;
    const { getToggleProps } = useToggleGroup({
      type,
      defaultValue,
      value,
      onChange: (value2) => onChange?.(value2)
    });
    const buttons = React19.useMemo(
      () => React19.Children.toArray(children).filter(React19.isValidElement).map((button, index, array) => {
        const isFirstItem = index === 0;
        const isLastItem = array.length === index + 1;
        const styleProps = isAttached ? Object.assign({
          ...isFirstItem && !isLastItem ? { borderRightRadius: 0 } : {},
          ...!isFirstItem && isLastItem ? { borderLeftRadius: 0 } : {},
          ...!isFirstItem && !isLastItem ? { borderRadius: 0 } : {},
          ...!isLastItem ? { mr: "-px" } : {}
        }) : {};
        return React19.cloneElement(button, {
          ...styleProps,
          type,
          toggleProps: getToggleProps({
            value: button.props.value,
            disabled: props.isDisabled || button.props.isDisabled
          })
        });
      }),
      [children, getToggleProps, props.isDisabled]
    );
    return /* @__PURE__ */ jsx38(
      ButtonGroup4,
      {
        isAttached,
        variant: "outline",
        ref,
        ...rest,
        children: buttons
      }
    );
  }
);
var ToggleButton = (props) => {
  const { toggleProps, type = "checkbox", ...rest } = props;
  const { getInputProps, getCheckboxProps, getLabelProps } = useCheckbox(toggleProps);
  const id = React19.useId();
  const inputProps = getInputProps();
  const buttonProps = getCheckboxProps();
  const labelProps = getLabelProps();
  return /* @__PURE__ */ jsxs20(Box, { as: "label", cursor: "pointer", ...labelProps, children: [
    /* @__PURE__ */ jsx38("input", { ...inputProps, type, "aria-labelledby": id }),
    /* @__PURE__ */ jsx38(Button5, { id, as: "div", display: "flex", ...buttonProps, ...rest })
  ] });
};
var CheckboxButtonGroupField = createField(
  forwardRef12((props, ref) => {
    return /* @__PURE__ */ jsx38(ToggleButtonGroup, { type: "checkbox", ...props, ref });
  }),
  {
    isControlled: true
  }
);
var RadioButtonGroupField = createField(
  forwardRef12((props, ref) => {
    return /* @__PURE__ */ jsx38(ToggleButtonGroup, { type: "radio", ...props, ref });
  }),
  {
    isControlled: true
  }
);

// src/index.ts
import {
  Beacon,
  BenefitsModal,
  BenefitsModalActions,
  BenefitsModalBody,
  BenefitsModalCloseButton,
  BenefitsModalContainer,
  BenefitsModalFooter,
  BenefitsModalHeader,
  BenefitsModalMedia,
  Tour,
  TourDialog,
  TourDialogActions,
  TourDialogAnchor,
  TourDialogArrow,
  TourDialogBody,
  TourDialogCloseButton,
  TourDialogContainer,
  TourDialogContextProvider,
  TourDialogFooter,
  TourDialogHeader,
  TourDialogPrimaryAction,
  TourDialogSecondaryAction,
  TourDialogTarget,
  TourDialogTrigger,
  TourDismissButton,
  TourNextButton,
  TourPrevButton,
  TourProvider,
  TourSpotlight,
  useTour,
  useTourBeacon,
  useTourContext,
  useTourDialog,
  useTourDialogContext,
  useTourSpotlight
} from "@saas-ui-pro/onboarding";

// src/radio-cards/use-radio-card.ts
import React20 from "react";
import { callAllHandlers as callAllHandlers4, dataAttr as dataAttr7 } from "@chakra-ui/utils";
import { nextById, prevById, queryAll } from "@zag-js/dom-utils";

// src/radio-cards/radio-cards-context.ts
import { createContext as createContext7 } from "@chakra-ui/utils";
var [RadioCardsProvider, useRadioCardsContext] = createContext7({
  errorMessage: "useRadioCardsContext: `context` is undefined. Seems you forgot to wrap component within <RadioCards />",
  strict: true
});

// src/radio-cards/use-radio-card.ts
var useRadioCard = (props) => {
  const { variant, size, colorScheme, value, setValue, containerRef } = useRadioCardsContext();
  const id = React20.useId();
  const cardId = props.id ?? id;
  const isChecked = value === props.value;
  function getItems() {
    return queryAll(
      containerRef.current,
      `.sui-radio-card:not([aria-disabled=true])`
    );
  }
  const onKeyDown = React20.useCallback(
    (e) => {
      const items = getItems();
      const prevItem = () => {
        const item = prevById(items, cardId, true);
        if (item) {
          item.focus();
          setValue(item.getAttribute("value"));
        }
      };
      const nextItem = () => {
        const item = nextById(items, cardId, true);
        if (item) {
          item.focus();
          setValue(item.getAttribute("value"));
        }
      };
      const keyMap = {
        ArrowUp: prevItem,
        ArrowDown: nextItem,
        ArrowLeft: prevItem,
        ArrowRight: nextItem
      };
      if (keyMap[e.key]) {
        e.preventDefault();
        keyMap[e.key](e);
      }
    },
    [cardId, setValue]
  );
  return {
    variant: props.variant ?? variant,
    size: props.size ?? size,
    colorScheme: props.colorScheme ?? colorScheme,
    rootProps: {
      id,
      role: "radio",
      tabIndex: isChecked ? 0 : -1,
      "data-checked": dataAttr7(isChecked),
      "aria-checked": isChecked,
      "aria-disabled": props.isDisabled,
      onClick: callAllHandlers4(props.onClick, () => setValue(props.value)),
      onKeyDown: callAllHandlers4(props.onKeyDown, onKeyDown)
    }
  };
};

// src/radio-cards/radio-cards-field.tsx
import { forwardRef as forwardRef14 } from "@chakra-ui/react";
import { createField as createField2 } from "@saas-ui/forms";

// src/radio-cards/radio-cards.tsx
import * as React21 from "react";
import {
  chakra as chakra19,
  forwardRef as forwardRef13,
  mergeRefs as mergeRefs4,
  useControllableState as useControllableState5,
  useStyleConfig as useStyleConfig3
} from "@chakra-ui/react";
import { cx as cx12, mapResponsive } from "@chakra-ui/utils";
import { jsx as jsx39 } from "react/jsx-runtime";
function countToColumns(count) {
  return mapResponsive(
    count,
    (value) => value === null ? null : `repeat(${value}, minmax(0, 1fr))`
  );
}
var RadioCards = forwardRef13((props, ref) => {
  const {
    children,
    defaultValue,
    value: valueProp,
    onChange,
    isDisabled,
    variant,
    size,
    colorScheme,
    ...rest
  } = props;
  const styles = useStyleConfig3("SuiRadioCards", props);
  const containerRef = React21.useRef(null);
  const [value, setValue] = useControllableState5({
    defaultValue,
    value: valueProp,
    onChange: (value2) => onChange?.(value2)
  });
  const context = {
    setValue,
    value,
    isDisabled,
    variant,
    size,
    colorScheme,
    containerRef
  };
  const rootStyles = {
    display: "grid",
    gridTemplateColumns: countToColumns(props.columns),
    gap: "4",
    ...styles
  };
  return /* @__PURE__ */ jsx39(RadioCardsProvider, { value: context, children: /* @__PURE__ */ jsx39(
    chakra19.div,
    {
      role: "radiogroup",
      ref: mergeRefs4(containerRef, ref),
      ...rest,
      className: cx12("sui-radio-group", rest.className),
      __css: rootStyles,
      children
    }
  ) });
});
var RadioCard = forwardRef13((props, ref) => {
  const { rootProps, variant, size, colorScheme } = useRadioCard(props);
  const styles = useStyleConfig3("SuiRadioCard", {
    ...props,
    variant,
    size,
    colorScheme
  });
  return /* @__PURE__ */ jsx39(
    chakra19.button,
    {
      ref,
      as: "button",
      ...rootProps,
      ...props,
      className: cx12("sui-radio-card", props.className),
      __css: styles,
      children: props.children
    }
  );
});

// src/radio-cards/radio-cards-field.tsx
import { jsx as jsx40 } from "react/jsx-runtime";
var RadioCardsField = createField2(
  forwardRef14((props, ref) => {
    return /* @__PURE__ */ jsx40(RadioCards, { ...props, ref });
  }),
  {
    isControlled: true
  }
);
export {
  ActiveFilter,
  ActiveFilterContainer,
  ActiveFilterLabel,
  ActiveFilterOperator,
  ActiveFilterProvider,
  ActiveFilterRemove,
  ActiveFilterValue,
  ActiveFilterValueInput,
  ActiveFiltersList,
  Aside,
  AsideBody,
  AsideContainer,
  AsideHeader,
  AsideTitle,
  BackButton,
  Beacon,
  BenefitsModal,
  BenefitsModalActions,
  BenefitsModalBody,
  BenefitsModalCloseButton,
  BenefitsModalContainer,
  BenefitsModalFooter,
  BenefitsModalHeader,
  BenefitsModalMedia,
  BulkActions,
  CheckboxButtonGroupField,
  Command,
  DataGrid,
  DataGridCheckbox,
  DataGridHeaderCell,
  DataGridPagination,
  DataGridPaginationNextButton,
  DataGridPaginationPageControl,
  DataGridPaginationPreviousButton,
  DataGridProvider,
  DataGridSort,
  DefaultDataGridCell,
  ErrorPage,
  FilterMenu,
  FiltersAddButton,
  FiltersProvider,
  MenuFilterItem,
  MenuInput,
  MenuProperty,
  MenuPropertyList,
  MotionBox,
  NoFilteredResults,
  NoResults,
  Page,
  PageBody,
  PageContainer,
  PageDescription,
  PageHeader,
  PageTitle,
  RadioButtonGroupField,
  RadioCard,
  RadioCards,
  RadioCardsField,
  ResetFilters,
  ResizeBox,
  ResizeHandle,
  Resizer,
  ResponsiveMenu,
  ResponsiveMenuList,
  Section,
  SectionBody,
  SectionContainer,
  SectionDescription,
  SectionHeader,
  SectionTitle,
  SplitPage,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  ToolbarButton,
  ToolbarDivider,
  ToolbarGroup,
  ToolbarToggleButton,
  ToolbarToggleGroup,
  Tour,
  TourDialog,
  TourDialogActions,
  TourDialogAnchor,
  TourDialogArrow,
  TourDialogBody,
  TourDialogCloseButton,
  TourDialogContainer,
  TourDialogContextProvider,
  TourDialogFooter,
  TourDialogHeader,
  TourDialogPrimaryAction,
  TourDialogSecondaryAction,
  TourDialogTarget,
  TourDialogTrigger,
  TourDismissButton,
  TourNextButton,
  TourPrevButton,
  TourProvider,
  TourSpotlight,
  createColumnHelper2 as createColumnHelper,
  createOperators,
  defaultOperators,
  getDataGridFilter,
  isElectron,
  platformSelect,
  theme,
  useActiveFilter,
  useActiveFilterContext,
  useColumnVisibility,
  useColumns,
  useDataGridContext,
  useDataGridFilter,
  useFilterItems,
  useFilterOperator,
  useFilterValue,
  useFilters,
  useFiltersContext,
  useRadioCard,
  useResize,
  useResizeContext,
  useSearchQuery,
  useSectionStyles,
  useSplitPage,
  useTour,
  useTourBeacon,
  useTourContext,
  useTourDialog,
  useTourDialogContext,
  useTourSpotlight,
  withThemeColors
};
//# sourceMappingURL=index.mjs.map